/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { prepareAssistantProductForm, readAssistantProductProposalReceipt, getAssistantProductFormUrl, assistantProductFormCopy } from './assistant-product-form';
import { AssistantDraftActionReview } from './AssistantDraftActionReview';
import { prepareAssistantDraftAction, readAssistantDraftActionResult, assistantDraftActionCopy, type AssistantDraftAction } from './assistant-draft-actions';
import { AssistantPurposeSelector } from './AssistantPurposeSelector';
import { AssistantWorkStatus } from './AssistantWorkStatus';
import { useAssistantWork } from './useAssistantWork';
import { AssistantWorkResponseError } from './assistant-work-client';
import { readAssistantPurpose, assistantPurposeCopy, assistantPurposePath, buildAssistantPurposeChatPayload, assertAssistantResponsePurpose, type AssistantConversationPurpose } from './assistant-conversation-purpose';
import { buildSupportFormDraft, supportTargetForPurpose } from './support-ticket-scope';
import { readAssistantSupportReceipt } from './assistant-support-receipt';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { Archive, Camera, ChevronLeft, ExternalLink, Flag, Maximize2, Menu, MessageSquarePlus, Minimize2, Paperclip, RotateCcw, Settings, Trash2, Volume2, VolumeX, X } from 'lucide-react';
import axios from '../../api/axios';
import { useDateTime } from '../../hooks/useDateTime';
import { Icon } from './ComponentIcon';
import {
  AssistantComposerBar,
  buildAssistantExpertPrompts,
  insertAssistantComposerMention,
  type AssistantComposerMention,
  type AssistantComposerMenuItem,
} from './AssistantComposerBar';
import { AssistantIdentityPicker, type AssistantIdentity } from './AssistantIdentityPicker';
import {
  AssistantTargetChip,
  AssistantWorkModeSelector,
  type AssistantWorkMode,
} from './AssistantWorkModeSelector';
import { showQubeProConfirmDialog, showQubeProNotice } from '../../utils/ui-feedback.utils';
import type { SupportedLanguage } from '../../utils/language.utils';
import { withViteBasePathForApiDms } from '../../utils/api-same-origin.utils';
import {
  buildAssistantAttachmentPayload,
  createAssistantId,
  getAssistantComposerActionDescriptors,
  getAssistantComposerTexts,
  normalizeAssistantSuggestedActions,
  resolveAssistantLanguageCode as resolveSharedAssistantLanguageCode,
  summarizeAssistantAttachments,
  type AssistantComposerActionDescriptor,
  type AssistantActionType,
  type AssistantAttachment,
  type AssistantSuggestedAction,
} from './AssistantRuntime';
import {
  SupportTicketForm,
  SUPPORT_FORM_INITIAL,
  supportFormHasContent,
  buildSupportDisplayMessage,
  type SupportTarget,
  type SupportFormState,
} from './SupportTicketForm';
import { readSupportSubmissionFailure, supportSubmissionErrorMessage, validateSupportForm, type SupportSubmissionFailure } from './support-ticket-validation';
import { captureAssistantPageScreenshot, assistantScreenCaptureCopy as getScreenCaptureLabels } from './assistant-page-screenshot';
import {
  createAssistantMessageTimestamp,
  normalizeAssistantMessageTimestamp,
} from './global-assistant-message.utils';
import {
  SemanticDataView as AssistantDataViewCard,
  SemanticDataSources as AssistantDataSources,
  normalizeSemanticDataPresentation as normalizeAssistantDataPresentation,
  normalizeSemanticDataSources as normalizeAssistantDataSources,
  normalizeSemanticDataView as normalizeAssistantDataView,
  normalizeSemanticVerifiedAnalysis as normalizeAssistantVerifiedAnalysis,
  type SemanticDataPresentation as AssistantDataPresentation,
  type SemanticDataViewModel as AssistantDataView,
  type SemanticDataSourceReference as AssistantDataSource,
  type SemanticVerifiedAnalysis as AssistantVerifiedAnalysis,
} from './SemanticDataView';
import { setQubeProFloatingSafeArea } from './floating-layout.utils';

/**
 * Panel UI tercihi — yerel depo (kurumsal isimlendirme `qubepro_<özellik>_<ayar>`,
 * ör. qubepro_theme). Yalnızca açık/geniş boolean'ı ve aktif conversation id; token/hassas veri DEĞİL.
 */
export const LS_AI_PANEL_OPEN = 'qubepro_ai_global_voice_panel_open';
export const GLOBAL_ASSISTANT_CONVERSATION_EVENT = 'qubepro:global-assistant-conversation-updated';
const LEGACY_LS_AI_PANEL_EXPANDED = 'qubepro_ai_global_voice_panel_expanded';
const LS_AI_ACTIVE_CONVERSATION_ID = 'qubepro_ai_global_voice_active_conversation_id';
const LS_AI_CONVERSATION_PURPOSE = 'qubepro_ai_global_voice_conversation_purpose';
const GLOBAL_ASSISTANT_BRAND_TITLE = 'QubePro AI';
const GLOBAL_ASSISTANT_FLOATING_SAFE_AREA_SOURCE = 'global-voice-assistant';
const GLOBAL_ASSISTANT_PANEL_MIN_WIDTH = 288;
const GLOBAL_ASSISTANT_PANEL_MAX_WIDTH = 640;
const GLOBAL_ASSISTANT_MAIN_CONTENT_MIN_WIDTH = 560;
const GLOBAL_ASSISTANT_PANEL_KEYBOARD_STEP = 24;

const LEGACY_AI_PANEL_PREF_KEYS: Record<string, string> = {
  [LS_AI_PANEL_OPEN]: 'qubepro_aiAssistant_panelOpen',
};

function getAiCookieDomainAttribute(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname.toLowerCase();
  if (!host || host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return '';
  return host === 'qubepro.com' || host.endsWith('.qubepro.com') ? '; Domain=.qubepro.com' : '';
}

function clearAiCookiePref(key: string): void {
  if (typeof document === 'undefined') return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = getAiCookieDomainAttribute();
  const encodedKey = encodeURIComponent(key);
  document.cookie = `${encodedKey}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  if (domain) {
    document.cookie = `${encodedKey}=; Path=/; Max-Age=0; SameSite=Lax${secure}${domain}`;
  }
}

function cleanupAiCookiePrefs(): void {
  clearAiCookiePref(LS_AI_PANEL_OPEN);
  clearAiCookiePref(LEGACY_LS_AI_PANEL_EXPANDED);
  clearAiCookiePref(LS_AI_ACTIVE_CONVERSATION_ID);
}

function cleanupLegacyExpandedPanelPref(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(LEGACY_LS_AI_PANEL_EXPANDED);
    window.localStorage.removeItem('qubepro_aiAssistant_panelExpanded');
    clearAiCookiePref(LEGACY_LS_AI_PANEL_EXPANDED);
  } catch {
    /* Eski tercih silinemiyorsa mevcut oturumun geçici genişliği yine etkilenmez. */
  }
}

export function readAiPanelPref(key: string): boolean {
  try {
    if (typeof window === 'undefined') return false;
    cleanupAiCookiePrefs();
    const direct = window.localStorage.getItem(key);
    if (direct != null) return direct === '1';
    const legacyKey = LEGACY_AI_PANEL_PREF_KEYS[key];
    if (!legacyKey) return false;
    const legacyValue = window.localStorage.getItem(legacyKey);
    if (legacyValue == null) return false;
    window.localStorage.setItem(key, legacyValue === '1' ? '1' : '0');
    window.localStorage.removeItem(legacyKey);
    return legacyValue === '1';
  } catch {
    return false;
  }
}

function writeAiPanelPref(key: string, value: boolean): void {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, value ? '1' : '0');
      const legacyKey = LEGACY_AI_PANEL_PREF_KEYS[key];
      if (legacyKey) window.localStorage.removeItem(legacyKey);
      cleanupAiCookiePrefs();
    }
  } catch {
    /* private mode / kota — yok say */
  }
}

function readAiStringPref(key: string): string {
  try {
    if (typeof window === 'undefined') return '';
    cleanupAiCookiePrefs();
    return String(window.localStorage.getItem(key) || '').trim();
  } catch {
    return '';
  }
}

function writeAiStringPref(key: string, value: string | number | null | undefined): void {
  try {
    if (typeof window === 'undefined') return;
    const normalized = value == null ? '' : String(value).trim().slice(0, 120);
    if (normalized) {
      window.localStorage.setItem(key, normalized);
    } else {
      window.localStorage.removeItem(key);
    }
    cleanupAiCookiePrefs();
  } catch {
    /* private mode / kota - yok say */
  }
}

interface GlobalAssistantPanelResizeSession {
  pointerId: number;
  startX: number;
  startWidth: number;
  rtl: boolean;
}

function resolveGlobalAssistantPanelResizeBounds(): { min: number; max: number } {
  const viewportWidth = typeof window === 'undefined'
    ? 1440
    : Math.max(0, window.innerWidth || document.documentElement.clientWidth);
  return {
    min: GLOBAL_ASSISTANT_PANEL_MIN_WIDTH,
    max: Math.max(
      resolveGlobalAssistantPresetWidth(true),
      Math.min(
        GLOBAL_ASSISTANT_PANEL_MAX_WIDTH,
        viewportWidth - GLOBAL_ASSISTANT_MAIN_CONTENT_MIN_WIDTH,
      ),
    ),
  };
}

function clampGlobalAssistantPanelWidth(value: number): number {
  const bounds = resolveGlobalAssistantPanelResizeBounds();
  return Math.max(bounds.min, Math.min(bounds.max, Math.round(value)));
}

function resolveGlobalAssistantPresetWidth(expanded: boolean): number {
  const viewportWidth = typeof window === 'undefined'
    ? 1440
    : Math.max(0, window.innerWidth || document.documentElement.clientWidth);
  const min = expanded ? 360 : 288;
  const max = expanded ? 520 : 360;
  const ratio = expanded ? 0.34 : 0.26;
  return Math.max(min, Math.min(max, Math.round(viewportWidth * ratio)));
}

/* qp-global-voice-* — TopHeaderToolbar ile qp-gva-* seçici çakışmasını önler */
const CSS = `
.qp-global-voice-root {
  --qp-global-voice-panel-width: clamp(288px, 26vw, 360px);
  flex: 0 0 var(--qp-global-voice-panel-width);
  width: var(--qp-global-voice-panel-width);
  min-width: 0;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  position: relative;
  padding: 8px 4px 8px 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  pointer-events: auto;
  container: qp-global-voice / inline-size;
}

.qp-global-voice-root,
.qp-global-voice-root *,
.qp-global-voice-root *::before,
.qp-global-voice-root *::after {
  box-sizing: border-box;
}

.qp-global-voice-root.expanded {
  --qp-global-voice-panel-width: clamp(360px, 34vw, 520px);
}

.qp-global-voice-root > * {
  pointer-events: auto;
}

.qp-global-voice-resize-handle {
  position: absolute;
  z-index: 2;
  inset-block: 18px;
  inset-inline-start: -4px;
  width: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: col-resize;
  touch-action: none;
  outline: none;
}

.qp-global-voice-resize-handle::before {
  content: '';
  position: absolute;
  inset-block: 12px;
  inset-inline-start: 3px;
  width: 2px;
  border-radius: 999px;
  background: var(--border-color);
  opacity: 0;
  transition: opacity 0.16s ease, background-color 0.16s ease;
}

.qp-global-voice-resize-handle:hover::before,
.qp-global-voice-resize-handle:focus-visible::before,
.qp-global-voice-root.resizing .qp-global-voice-resize-handle::before {
  background: var(--accent-primary);
  opacity: 1;
}

.qp-global-voice-root.resizing .qp-global-voice-panel,
.layout.qp-global-voice-legacy-resizing > .layout-content-wrapper > .main-content {
  transition: none;
}

body.qp-global-voice-resizing,
body.qp-global-voice-resizing * {
  cursor: col-resize;
  user-select: none;
}

[dir="rtl"] .qp-global-voice-root {
  padding: 8px 0 8px 4px;
}

@media (min-width: 769px) {
  .layout.qp-global-voice-legacy-docked {
    --qp-global-voice-panel-width: clamp(288px, 26vw, 360px);
  }

  .layout.qp-global-voice-legacy-docked.qp-global-voice-legacy-expanded {
    --qp-global-voice-panel-width: clamp(360px, 34vw, 520px);
  }

  .layout.qp-global-voice-legacy-docked > .qp-global-voice-root {
    position: fixed;
    top: var(--top-header-height, 72px);
    right: 0;
    bottom: 0;
    z-index: 1000;
    flex: none;
    width: var(--qp-global-voice-panel-width);
    height: auto;
    padding: 8px 4px 8px 0;
  }

  .layout.qp-global-voice-legacy-docked > .qp-global-voice-root.expanded {
    width: var(--qp-global-voice-panel-width);
  }

  .layout.qp-global-voice-legacy-docked > .layout-content-wrapper > .main-content {
    margin-right: var(--qp-global-voice-panel-width);
    min-width: 0;
    transition: margin-right 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  }

  [dir="rtl"] .layout.qp-global-voice-legacy-docked > .qp-global-voice-root {
    right: auto;
    left: 0;
    padding: 8px 0 8px 4px;
  }

  [dir="rtl"] .layout.qp-global-voice-legacy-docked > .layout-content-wrapper > .main-content {
    margin-right: 0;
    margin-left: var(--qp-global-voice-panel-width);
  }
}

.qp-global-voice-panel {
  width: 100%;
  height: 100%;
  max-height: none;
  box-sizing: border-box;
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: none;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: opacity 0.25s ease, transform 0.25s ease, width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  transform: translateX(16px);
  pointer-events: none;
}

.qp-global-voice-panel.open {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

/* "Genişlet" (Gmail/Gemini) — daha geniş çalışma alanı */
.qp-global-voice-panel.expanded {
  width: 100%;
}

@container qp-global-voice (max-width: 320px) {
  .qp-global-voice-header {
    padding: 12px;
  }

  .qp-global-voice-body {
    padding: 12px;
    gap: 10px;
  }

  .qp-global-voice-msg-wrap {
    max-width: 90%;
  }

  .qp-global-voice-msg {
    padding: 9px 12px;
    font-size: 13px;
  }

  .qp-global-voice-footer {
    padding: 9px;
  }

  .qp-global-voice-input-shell {
    min-height: 44px;
    padding-inline: 12px;
  }
}

.qp-global-voice-header {
  padding: 14px;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}

.qp-global-voice-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.qp-global-voice-menu-toggle {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qp-global-voice-menu-toggle:hover,
.qp-global-voice-menu-toggle.active {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.qp-global-voice-heading {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qp-global-voice-close {
  width: 32px;
  height: 32px;
  background: transparent;
  border: 0;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  border-radius: 8px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.qp-global-voice-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.qp-global-voice-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.qp-global-voice-audio-toggle {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.qp-global-voice-audio-toggle:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
}

.qp-global-voice-audio-toggle.active {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
}

.qp-global-voice-nav-panel {
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.qp-global-voice-nav-panel.history-mode {
  max-height: min(58vh, 430px);
}

.qp-global-voice-nav-panel-head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
}

.qp-global-voice-nav-back {
  width: 32px;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qp-global-voice-nav-back:hover:not(:disabled) {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.qp-global-voice-nav-title {
  min-width: 0;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qp-global-voice-history-filters {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qp-global-voice-history-filter-group {
  min-width: 0;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-primary);
  padding: 1px;
  display: flex;
  gap: 1px;
  overflow: hidden;
}

.qp-global-voice-history-filter-group .qp-global-voice-history-filter {
  flex: 1 1 0;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.15;
  min-height: 26px;
  padding: 3px 7px;
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.qp-global-voice-history-filter-group .qp-global-voice-history-filter:hover:not(:disabled) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.qp-global-voice-history-filter-group .qp-global-voice-history-filter.active {
  border-color: color-mix(in srgb, var(--accent-primary) 42%, var(--border-color));
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.qp-global-voice-history-filter-group .qp-global-voice-history-filter.active::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: var(--accent-primary);
  flex: 0 0 auto;
}

.qp-global-voice-nav-command {
  width: 100%;
  min-height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  box-sizing: border-box;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.qp-global-voice-nav-command:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.qp-global-voice-nav-command span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qp-global-voice-nav-command:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qp-global-voice-nav-command.primary {
  border-color: color-mix(in srgb, var(--accent-primary) 42%, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
}

.qp-global-voice-nav-command.primary svg {
  color: var(--accent-primary);
}

@media (max-width: 420px) {
  .qp-global-voice-history-filters {
    gap: 6px;
  }
}

.qp-global-voice-nav-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qp-global-voice-nav-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
}

.qp-global-voice-nav-link {
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 2px 0;
  text-decoration: none;
}

.qp-global-voice-nav-link:hover {
  color: var(--text-primary);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.qp-global-voice-nav-history-list {
  max-height: 174px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.qp-global-voice-nav-history-item {
  width: 100%;
  min-height: 42px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 7px 8px;
  text-align: left;
  box-sizing: border-box;
}

.qp-global-voice-nav-history-item:hover {
  border-color: var(--accent-primary);
}

.qp-global-voice-nav-history-name,
.qp-global-voice-nav-history-meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.qp-global-voice-nav-history-name {
  font-size: 12px;
  font-weight: 600;
}

.qp-global-voice-nav-history-meta {
  color: var(--text-secondary);
  font-size: 11px;
}

.qp-global-voice-nav-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.qp-global-voice-history-panel {
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  flex-shrink: 0;
}

.qp-global-voice-history-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.qp-global-voice-history-title {
  min-width: 0;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
}

.qp-global-voice-history-filter {
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 12px;
  line-height: 1.2;
  padding: 5px 9px;
}

.qp-global-voice-history-filter:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.qp-global-voice-history-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.qp-global-voice-nav-panel .qp-global-voice-history-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.qp-global-voice-history-item {
  width: 100%;
  min-height: 44px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 7px 8px;
  box-sizing: border-box;
}

.qp-global-voice-history-main {
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  line-height: 1.2;
  padding: 0;
  text-align: left;
}

.qp-global-voice-history-name,
.qp-global-voice-history-meta {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.qp-global-voice-history-name {
  font-size: 12px;
  font-weight: 600;
}

.qp-global-voice-history-meta {
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 11px;
}

.qp-global-voice-history-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.qp-global-voice-icon-btn {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.qp-global-voice-icon-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.qp-global-voice-icon-btn.active {
  color: var(--accent-primary);
}

.qp-global-voice-icon-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qp-global-voice-empty {
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.35;
}

.qp-global-voice-body {
  flex: 1;
  min-height: 0;
  padding: 14px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--bg-primary);
}

.qp-global-voice-msg {
  max-width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.qp-global-voice-msg-wrap {
  max-width: 85%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.qp-global-voice-msg-wrap.user {
  align-self: flex-end;
  align-items: flex-end;
}

.qp-global-voice-msg-wrap.ai {
  align-self: flex-start;
  align-items: flex-start;
}

.qp-global-voice-routing-label {
  max-width: 100%;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

.qp-global-voice-expert-contributions {
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.qp-global-voice-expert-contribution {
  min-height: 24px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  font-size: 10px;
  line-height: 1.1;
}

.qp-global-voice-expert-contribution.completed {
  border-color: var(--control-selected-border, var(--border-color));
  background: var(--control-selected-bg, var(--bg-tertiary));
  color: var(--control-selected-text, var(--text-primary));
}

.qp-global-voice-msg.user {
  border: 1px solid var(--chat-user-message-border);
  background: var(--chat-user-message-bg);
  color: var(--chat-user-message-text);
  border-bottom-right-radius: 4px;
}

.qp-global-voice-msg.ai {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-bottom-left-radius: 4px;
}

.qp-global-voice-msg-time {
  color: var(--text-secondary);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  padding: 0 4px;
  white-space: nowrap;
}

.qp-global-voice-actions,
.qp-global-voice-quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
}

.qp-global-voice-quick-actions {
  align-self: flex-start;
}

.qp-global-voice-action-btn {
  min-height: 32px;
  max-width: 100%;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.2;
  cursor: pointer;
  white-space: normal;
  text-align: center;
}

.qp-global-voice-action-btn:hover:not(:disabled) {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.qp-global-voice-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qp-global-voice-processing {
  font-size: 12px;
  opacity: 0.7;
}

.qp-global-voice-footer {
  padding: 10px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.qp-global-voice-footer-input-row {
  min-width: 0;
}

.qp-global-voice-footer-actions {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  container-type: inline-size;
}

.qp-global-voice-footer-secondary {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.qp-global-voice-footer-actions > .assistant-work-mode {
  min-width: 0;
}

.qp-global-voice-footer-secondary .qp-global-voice-audio-toggle {
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
}

.qp-global-voice-footer-secondary .qp-global-voice-audio-toggle.active {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
}

.qp-global-voice-plus-wrap {
  position: relative;
  width: 36px;
  height: 36px;
}

.qp-global-voice-plus-menu {
  z-index: 3;
}

.qp-global-voice-file-input {
  display: none;
}

.qp-global-voice-attachments {
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  padding: 8px 12px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  box-sizing: border-box;
}

.qp-global-voice-attachment-chip {
  max-width: 100%;
  min-height: 30px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 9px;
  font-size: 12px;
  line-height: 1.2;
}

.qp-global-voice-attachment-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qp-global-voice-attachment-remove {
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.qp-global-voice-attachment-remove:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.qp-global-voice-support-hint {
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  padding: 8px 12px 0;
  font-size: 12px;
  line-height: 1.35;
}

.qp-global-voice-input-shell {
  width: 100%;
  min-width: 0;
  min-height: 52px;
  max-height: 52px;
  box-sizing: border-box;
  padding: 6px 2px;
  border-radius: 0;
  border: 0;
  background: transparent;
  display: block;
  overflow-y: auto;
  line-height: 20px;
}

.qp-global-voice-input-shell:focus-within {
  border-color: transparent;
}

.qp-global-voice-input {
  border: none;
  background: transparent;
  color: var(--text-primary);
  outline: none;
  line-height: 20px;
  font-size: 14px;
  padding: 0;
  font-family: inherit;
}

.qp-global-voice-input[contenteditable] {
  display: inline;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.qp-global-voice-input[contenteditable]:empty::before {
  content: attr(data-placeholder);
  color: var(--text-secondary);
  pointer-events: none;
}

.qp-global-voice-input[contenteditable][data-disabled='true'] {
  opacity: 0.6;
  cursor: not-allowed;
}

.qp-global-voice-input-shell .assistant-target-chip {
  margin-inline-end: 2px;
  vertical-align: top;
}

.qp-global-voice-inline-btn {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.qp-global-voice-inline-btn:hover:not(:disabled) {
  color: var(--text-primary);
  background: var(--bg-tertiary);
  box-shadow: var(--shadow-sm);
}

.qp-global-voice-inline-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qp-global-voice-inline-btn.listening {
  background: var(--stat-icon-danger);
  color: var(--text-on-accent);
  animation: qp-global-voice-mic-pulse 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}

.qp-global-voice-send-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: var(--text-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.16s ease, opacity 0.16s ease, transform 0.16s ease;
}

.qp-global-voice-send-btn:hover:not(:disabled) {
  background: transparent;
  color: var(--accent-primary);
}

.qp-global-voice-send-btn:focus-visible:not(:disabled) {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

.qp-global-voice-send-btn:disabled {
  background: transparent;
  color: var(--text-secondary);
  cursor: not-allowed;
}

.qp-global-voice-send-btn[data-state='idle'] {
  display: none;
  opacity: 0;
  pointer-events: none;
  transform: scale(0.88);
}

.qp-global-voice-send-btn[data-state='ready'],
.qp-global-voice-send-btn[data-state='stop'] {
  display: inline-flex;
  opacity: 1;
  transform: scale(1);
}

.qp-global-voice-send-btn[data-state='stop'] {
  color: var(--text-primary);
}

.qp-global-voice-mic-btn {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.qp-global-voice-mic-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
}

.qp-global-voice-mic-btn.active {
  border-radius: 999px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
}

.qp-global-voice-audio-toggle:focus-visible,
.qp-global-voice-inline-btn:focus-visible,
.qp-global-voice-mic-btn:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

.qp-global-voice-mic-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qp-global-voice-mic-btn.listening {
  background: var(--status-canceled);
  border-color: var(--status-canceled);
  color: var(--text-on-accent);
  animation: qp-global-voice-mic-pulse 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}

@keyframes qp-global-voice-mic-pulse {
  0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--status-canceled) 50%, transparent); }
  70% { box-shadow: 0 0 0 10px color-mix(in srgb, var(--status-canceled) 0%, transparent); }
  100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--status-canceled) 0%, transparent); }
}

@media (max-width: 768px) {
  .qp-global-voice-resize-handle {
    display: none;
  }

  .qp-global-voice-root {
    position: fixed;
    top: var(--mobile-top-header-height, 72px);
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    left: 16px;
    z-index: 9999;
    width: auto;
    height: auto;
    padding: 0;
    flex-basis: auto;
    align-items: stretch;
  }

  .qp-global-voice-panel,
  .qp-global-voice-panel.expanded {
    width: 100%;
    height: 100%;
    max-height: none;
  }
}

@media (max-width: 480px) {
  .qp-global-voice-root {
    inset: 0;
    width: auto;
    height: auto;
    justify-content: flex-end;
    align-items: stretch;
  }

  .qp-global-voice-panel {
    width: 100%;
    height: calc(100dvh - 12px);
    max-height: none;
    border-right: none;
    border-left: none;
    border-bottom: none;
    border-radius: 16px 16px 0 0;
    transform: translateY(16px) scale(0.98);
  }

  .qp-global-voice-panel.open {
    transform: translateY(0) scale(1);
  }

  .qp-global-voice-header {
    padding: 12px 14px;
  }

  .qp-global-voice-title {
    font-size: 15px;
  }

  .qp-global-voice-body {
    padding: 12px;
    gap: 10px;
  }

  .qp-global-voice-msg-wrap {
    max-width: 92%;
  }

  .qp-global-voice-msg {
    padding: 9px 12px;
    font-size: 13px;
  }

  .qp-global-voice-footer {
    gap: 8px;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  }

  .qp-global-voice-plus-wrap,
  .qp-global-voice-mic-btn,
  .qp-global-voice-inline-btn {
    height: 40px;
    width: 40px;
  }

  .qp-global-voice-input-shell {
    min-height: 44px;
    border-radius: 16px;
  }

  .qp-global-voice-send-btn {
    height: 40px;
    width: 40px;
  }

  .qp-global-voice-attachments {
    padding: 8px 12px 0;
  }
}
`;

function normalizeAssistantLanguage(language?: string): string {
  const explicit = (language || '').trim().toLowerCase();
  if (explicit) return explicit;
  if (typeof document !== 'undefined') {
    const html = (document.documentElement.lang || '').trim().toLowerCase();
    if (html) return html;
  }
  try {
    const lng = (
      localStorage.getItem('i18nextLng') ||
      localStorage.getItem('qubepro_i18nextLng') ||
      ''
    )
      .trim()
      .toLowerCase();
    if (lng) return lng;
  } catch {
    /* private mode */
  }
  return (typeof navigator !== 'undefined' ? navigator.language : 'tr').toLowerCase();
}

type AssistantLanguageCode = 'tr' | 'en' | 'ar' | 'de' | 'fr' | 'es' | 'it' | 'ru' | 'zh' | 'ja';

type AssistantTexts = {
  title: string;
  welcome: string;
  unsupported: string;
  micDenied: string;
  speechNetwork: string;
  speechStartFailed: string;
  typingPlaceholder: string;
  micTitle: string;
  micAria: string;
  processing: string;
  fallbackResponse: string;
  suggestedAction: string;
  quickHelp: string;
  quickSupport: string;
  quickReport: string;
  quickAutomation: string;
  composerMenuLabel?: string;
  attachFileLabel?: string;
  attachFileDescription?: string;
  attachFileAddedMessage?: string;
  confirmAction: string;
  actionSuccess: string;
  actionFailed: string;
  actionNeedsDraft: string;
  unavailable: string;
  supportTypingPlaceholder?: string;
  supportDescriptionHint?: string;
  supportDescriptionFocusMessage?: string;
  supportMenuMessage?: string;
  supportWritePrompt?: string;
  supportDraftReadyMessage?: string;
  supportMainMenuMessage?: string;
  supportAddDescriptionLabel?: string;
  supportAddImageLabel?: string;
  supportSendLabel?: string;
  supportMainMenuLabel?: string;
  historyTitle?: string;
  historyOpenTitle?: string;
  newConversationTitle?: string;
  archiveConversationTitle?: string;
  unarchiveConversationTitle?: string;
  deleteConversationTitle?: string;
  showArchivedTitle?: string;
  showActiveTitle?: string;
  noHistoryMessage?: string;
  historyLoadFailed?: string;
  speechReadOnTitle: string;
  speechReadOffTitle: string;
  speechReadOnAria: string;
  speechReadOffAria: string;
};

const ASSISTANT_LANGUAGE_CONFIG: Record<AssistantLanguageCode, { voiceLocale: string; texts: AssistantTexts }> = {
  tr: {
    voiceLocale: 'tr-TR',
    texts: {
      title: 'QubePro İş Asistanı',
      welcome:
        'Merhaba! Ben QubePro İş Asistanınız. Size nasıl yardımcı olabilirim? Yazarak veya sesli komutla istekte bulunabilirsiniz.',
      unsupported: 'Tarayıcınız sesli yazmayı desteklemiyor. Chrome, Edge veya Safari kullanın.',
      micDenied:
        'Mikrofon izni verilmedi veya engellendi. Site için mikrofonu açın; mümkünse HTTPS (localhost dışında) kullanın.',
      speechNetwork:
        'Ses tanıma ağına ulaşılamadı. İnternet bağlantınızı ve kurumsal güvenlik duvarını kontrol edin.',
      speechStartFailed: 'Ses tanıma başlatılamadı. Başka sekmede mikrofon kullanılıyor olabilir.',
      typingPlaceholder: 'Asistana bir şey yazın...',
      micTitle: 'Sesli yaz - konuşun; metin kutusunda canlı görünür. Tekrar tıklayınca durur.',
      micAria: 'Sesli yaz',
      processing: 'Yanıtlanıyor...',
      fallbackResponse: 'Üzgünüm, şu an yanıt veremiyorum.',
      suggestedAction: 'Önerilen işlem',
      quickHelp: 'Bu ekranı açıkla',
      quickSupport: 'Destek kaydı aç',
      quickReport: 'Rapor taslağı hazırla',
      quickAutomation: 'Otomasyon taslağı hazırla',
      composerMenuLabel: 'Yetenekler ve ekler',
      attachFileLabel: 'Dosya ekle',
      attachFileDescription: 'Doküman veya görsel ekle',
      attachFileAddedMessage: 'Dosya eklendi. Destek kaydıyla birlikte iletilecek.',
      confirmAction: 'Bu işlemi onaylıyor musunuz?',
      actionSuccess: 'İşlem tamamlandı.',
      actionFailed: 'İşlem tamamlanamadı.',
      actionNeedsDraft: 'Bu işlem için AI taslak verisi gelmedi. İsteğinizi biraz daha detaylandırıp tekrar deneyin.',
      unavailable: 'Şu an asistan servisine bağlanılamıyor.',
      supportTypingPlaceholder: 'Sorununuzu buraya yazın...',
      supportDescriptionHint: 'Destek kaydı için sorunu, beklediğiniz davranışı ve varsa hata mesajını buraya yazın.',
      supportDescriptionFocusMessage: 'Sorununuzu destek formundaki açıklama alanına yazabilirsiniz. Kaydı göndermeden önce içeriği inceleyebilirsiniz.',
      supportMenuMessage:
        'Destek kaydı menüsündesiniz. Sorunu yazabilir, resim ekleyebilir veya hazır olduğunuzda kaydı gönderebilirsiniz.',
      supportWritePrompt:
        'Destek kaydı oluşturuyoruz. Lütfen sorununuzu alttaki alana yazın: ne oldu, ne bekliyordunuz, varsa hata mesajı. Yazıp gönderdikten sonra resim ekleyip kaydı oluşturabilirsiniz.',
      supportDraftReadyMessage:
        'Açıklamanızı aldım. Kaydı gönderebilir, resim ekleyebilir veya açıklamayı güncelleyebilirsiniz.',
      supportMainMenuMessage: 'Ana menüye döndüm.',
      supportAddDescriptionLabel: 'Açıklama ekle',
      supportAddImageLabel: 'Ekran görüntüsü ekle',
      supportSendLabel: 'Kaydı gönder',
      supportMainMenuLabel: 'Ana menüye git',
      historyTitle: 'Geçmiş',
      historyOpenTitle: 'Konuşma geçmişi',
      newConversationTitle: 'Yeni konuşma',
      archiveConversationTitle: 'Arşivle',
      unarchiveConversationTitle: 'Arşivden çıkar',
      deleteConversationTitle: 'Sil',
      showArchivedTitle: 'Arşiv',
      showActiveTitle: 'Aktif',
      noHistoryMessage: 'Kayıtlı konuşma bulunmuyor.',
      historyLoadFailed: 'Konuşma geçmişi alınamadı.',
      speechReadOnTitle: 'Sesli okuma açık - kapat',
      speechReadOffTitle: 'Sesli okuma kapalı - aç',
      speechReadOnAria: 'Sesli okumayı kapat',
      speechReadOffAria: 'Sesli okumayı aç',
    },
  },
  en: {
    voiceLocale: 'en-US',
    texts: {
      title: 'QubePro Work Assistant',
      welcome: 'Hello! I am your QubePro Work Assistant. How can I help? You can type or use voice commands.',
      unsupported: 'Your browser does not support voice typing. Use Chrome, Edge, or Safari.',
      micDenied:
        'Microphone permission was denied or blocked. Enable the microphone for this site; use HTTPS outside localhost if possible.',
      speechNetwork: 'Speech recognition could not reach the network. Check your internet connection and corporate firewall.',
      speechStartFailed: 'Speech recognition could not be started. Another tab may be using the microphone.',
      typingPlaceholder: 'Type something to the assistant...',
      micTitle: 'Voice typing - speak and the text appears live. Click again to stop.',
      micAria: 'Voice typing',
      processing: 'Answering...',
      fallbackResponse: 'Sorry, I cannot answer right now.',
      suggestedAction: 'Suggested action',
      quickHelp: 'Explain this screen',
      quickSupport: 'Open support ticket',
      quickReport: 'Prepare report draft',
      quickAutomation: 'Prepare automation draft',
      composerMenuLabel: 'Tools and attachments',
      attachFileLabel: 'Attach file',
      attachFileDescription: 'Attach a document or image',
      attachFileAddedMessage: 'File attached. It will be sent with the support ticket.',
      confirmAction: 'Do you confirm this action?',
      actionSuccess: 'Action completed.',
      actionFailed: 'Action could not be completed.',
      actionNeedsDraft: 'The AI did not provide draft data for this action. Add a little more detail and try again.',
      unavailable: 'The assistant service is currently unavailable.',
      supportTypingPlaceholder: 'Describe the issue here...',
      supportDescriptionHint: 'For the support ticket, describe the issue, expected behavior, and any error message here.',
      supportDescriptionFocusMessage: 'Describe the issue in the support form. You can review its content before submitting the ticket.',
      supportMenuMessage:
        'You are in the support ticket menu. Describe the issue, attach a screenshot, or send the ticket when ready.',
      supportDraftReadyMessage:
        'I captured your description. You can send the ticket, attach a screenshot, or update the description.',
      supportMainMenuMessage: 'Back to the main menu.',
      supportAddDescriptionLabel: 'Add description',
      supportAddImageLabel: 'Attach image',
      supportSendLabel: 'Send',
      supportMainMenuLabel: 'Main menu',
      historyTitle: 'History',
      historyOpenTitle: 'Conversation history',
      newConversationTitle: 'New conversation',
      archiveConversationTitle: 'Archive',
      unarchiveConversationTitle: 'Unarchive',
      deleteConversationTitle: 'Delete',
      showArchivedTitle: 'Archive',
      showActiveTitle: 'Active',
      noHistoryMessage: 'No saved conversations.',
      historyLoadFailed: 'Conversation history could not be loaded.',
      speechReadOnTitle: 'Voice reading is on - turn off',
      speechReadOffTitle: 'Voice reading is off - turn on',
      speechReadOnAria: 'Turn voice reading off',
      speechReadOffAria: 'Turn voice reading on',
    },
  },
  ar: {
    voiceLocale: 'ar-SA',
    texts: {
      title: 'مساعد أعمال QubePro',
      welcome: 'مرحبا! أنا مساعد العمل في QubePro. كيف يمكنني مساعدتك؟ يمكنك الكتابة أو استخدام الأوامر الصوتية.',
      unsupported: 'المتصفح لا يدعم الإملاء الصوتي. استخدم Chrome أو Edge أو Safari.',
      micDenied: 'تم رفض أو حظر إذن الميكروفون. فعّل الميكروفون لهذا الموقع؛ واستخدم HTTPS خارج localhost إن أمكن.',
      speechNetwork: 'تعذر الوصول إلى خدمة التعرف على الصوت. تحقق من الاتصال بالإنترنت وجدار الحماية.',
      speechStartFailed: 'تعذر بدء التعرف على الصوت. قد تكون علامة تبويب أخرى تستخدم الميكروفون.',
      typingPlaceholder: 'اكتب شيئا للمساعد...',
      micTitle: 'إملاء صوتي - تحدث وسيظهر النص مباشرة. انقر مرة أخرى للإيقاف.',
      micAria: 'إملاء صوتي',
      processing: 'جار الإجابة...',
      fallbackResponse: 'عذرا، لا أستطيع الرد الآن.',
      suggestedAction: 'الإجراء المقترح',
      quickHelp: 'اشرح هذه الشاشة',
      quickSupport: 'فتح تذكرة دعم',
      quickReport: 'إعداد مسودة تقرير',
      quickAutomation: 'إعداد مسودة أتمتة',
      confirmAction: 'هل تؤكد هذا الإجراء؟',
      actionSuccess: 'تم تنفيذ الإجراء.',
      actionFailed: 'تعذر تنفيذ الإجراء.',
      actionNeedsDraft: 'لم يرسل الذكاء الاصطناعي بيانات مسودة لهذا الإجراء. أضف تفاصيل أكثر وحاول مرة أخرى.',
      unavailable: 'خدمة المساعد غير متاحة حاليا.',
      speechReadOnTitle: 'القراءة الصوتية مفعلة - إيقاف',
      speechReadOffTitle: 'القراءة الصوتية متوقفة - تشغيل',
      speechReadOnAria: 'إيقاف القراءة الصوتية',
      speechReadOffAria: 'تشغيل القراءة الصوتية',
    },
  },
  de: {
    voiceLocale: 'de-DE',
    texts: {
      title: 'QubePro Arbeitsassistent',
      welcome:
        'Hallo! Ich bin Ihr QubePro Arbeitsassistent. Wie kann ich helfen? Sie können schreiben oder Sprachbefehle verwenden.',
      unsupported: 'Ihr Browser unterstützt Spracheingabe nicht. Verwenden Sie Chrome, Edge oder Safari.',
      micDenied:
        'Die Mikrofonberechtigung wurde verweigert oder blockiert. Aktivieren Sie das Mikrofon für diese Site; außerhalb von localhost möglichst HTTPS verwenden.',
      speechNetwork: 'Die Spracherkennung konnte das Netzwerk nicht erreichen. Prüfen Sie Internetverbindung und Firewall.',
      speechStartFailed: 'Die Spracherkennung konnte nicht gestartet werden. Möglicherweise nutzt ein anderer Tab das Mikrofon.',
      typingPlaceholder: 'Schreiben Sie dem Assistenten...',
      micTitle: 'Spracheingabe - sprechen Sie, der Text erscheint live. Erneut klicken zum Stoppen.',
      micAria: 'Spracheingabe',
      processing: 'Antwort wird erstellt...',
      fallbackResponse: 'Entschuldigung, ich kann im Moment nicht antworten.',
      suggestedAction: 'Vorgeschlagene Aktion',
      quickHelp: 'Diese Ansicht erklären',
      quickSupport: 'Supportticket öffnen',
      quickReport: 'Berichtsentwurf erstellen',
      quickAutomation: 'Automationsentwurf erstellen',
      confirmAction: 'Möchten Sie diese Aktion bestätigen?',
      actionSuccess: 'Aktion abgeschlossen.',
      actionFailed: 'Aktion konnte nicht abgeschlossen werden.',
      actionNeedsDraft: 'Die KI hat keine Entwurfsdaten für diese Aktion geliefert. Bitte beschreiben Sie den Wunsch genauer.',
      unavailable: 'Der Assistentendienst ist derzeit nicht verfügbar.',
      speechReadOnTitle: 'Vorlesen ist aktiviert - ausschalten',
      speechReadOffTitle: 'Vorlesen ist deaktiviert - einschalten',
      speechReadOnAria: 'Vorlesen ausschalten',
      speechReadOffAria: 'Vorlesen einschalten',
    },
  },
  fr: {
    voiceLocale: 'fr-FR',
    texts: {
      title: 'Assistant de travail QubePro',
      welcome:
        'Bonjour ! Je suis votre assistant de travail QubePro. Comment puis-je vous aider ? Vous pouvez écrire ou utiliser des commandes vocales.',
      unsupported: 'Votre navigateur ne prend pas en charge la saisie vocale. Utilisez Chrome, Edge ou Safari.',
      micDenied:
        'L’autorisation du microphone a été refusée ou bloquée. Activez le microphone pour ce site ; utilisez HTTPS hors localhost si possible.',
      speechNetwork: 'La reconnaissance vocale n’a pas pu accéder au réseau. Vérifiez votre connexion et le pare-feu.',
      speechStartFailed: 'La reconnaissance vocale n’a pas pu démarrer. Un autre onglet utilise peut-être le microphone.',
      typingPlaceholder: 'Écrivez quelque chose à l’assistant...',
      micTitle: 'Saisie vocale - parlez et le texte apparaît en direct. Cliquez à nouveau pour arrêter.',
      micAria: 'Saisie vocale',
      processing: 'Réponse en cours...',
      fallbackResponse: 'Désolé, je ne peux pas répondre pour le moment.',
      suggestedAction: 'Action suggérée',
      quickHelp: 'Expliquer cet écran',
      quickSupport: 'Ouvrir un ticket support',
      quickReport: 'Préparer un brouillon de rapport',
      quickAutomation: 'Préparer un brouillon d’automatisation',
      confirmAction: 'Confirmez-vous cette action ?',
      actionSuccess: 'Action terminée.',
      actionFailed: 'L’action n’a pas pu être terminée.',
      actionNeedsDraft: 'L’IA n’a pas fourni de brouillon pour cette action. Ajoutez plus de détails puis réessayez.',
      unavailable: 'Le service assistant est actuellement indisponible.',
      speechReadOnTitle: 'Lecture vocale activée - désactiver',
      speechReadOffTitle: 'Lecture vocale désactivée - activer',
      speechReadOnAria: 'Désactiver la lecture vocale',
      speechReadOffAria: 'Activer la lecture vocale',
    },
  },
  es: {
    voiceLocale: 'es-ES',
    texts: {
      title: 'Asistente de trabajo QubePro',
      welcome:
        'Hola. Soy su asistente de trabajo QubePro. ¿Cómo puedo ayudarle? Puede escribir o usar comandos de voz.',
      unsupported: 'Su navegador no admite dictado por voz. Use Chrome, Edge o Safari.',
      micDenied:
        'El permiso del micrófono fue denegado o bloqueado. Active el micrófono para este sitio; use HTTPS fuera de localhost si es posible.',
      speechNetwork: 'El reconocimiento de voz no pudo acceder a la red. Revise su conexión y el firewall corporativo.',
      speechStartFailed: 'No se pudo iniciar el reconocimiento de voz. Otra pestaña puede estar usando el micrófono.',
      typingPlaceholder: 'Escriba algo al asistente...',
      micTitle: 'Dictado por voz - hable y el texto aparecerá en vivo. Haga clic de nuevo para detener.',
      micAria: 'Dictado por voz',
      processing: 'Respondiendo...',
      fallbackResponse: 'Lo siento, ahora no puedo responder.',
      suggestedAction: 'Acción sugerida',
      quickHelp: 'Explicar esta pantalla',
      quickSupport: 'Abrir ticket de soporte',
      quickReport: 'Preparar borrador de informe',
      quickAutomation: 'Preparar borrador de automatización',
      confirmAction: '¿Confirma esta acción?',
      actionSuccess: 'Acción completada.',
      actionFailed: 'No se pudo completar la acción.',
      actionNeedsDraft: 'La IA no proporcionó datos de borrador para esta acción. Agregue más detalle e inténtelo de nuevo.',
      unavailable: 'El servicio del asistente no está disponible en este momento.',
      speechReadOnTitle: 'Lectura por voz activada - desactivar',
      speechReadOffTitle: 'Lectura por voz desactivada - activar',
      speechReadOnAria: 'Desactivar lectura por voz',
      speechReadOffAria: 'Activar lectura por voz',
    },
  },
  it: {
    voiceLocale: 'it-IT',
    texts: {
      title: 'Assistente di lavoro QubePro',
      welcome:
        'Ciao! Sono il tuo assistente di lavoro QubePro. Come posso aiutarti? Puoi scrivere o usare comandi vocali.',
      unsupported: 'Il browser non supporta la dettatura vocale. Usa Chrome, Edge o Safari.',
      micDenied:
        'Il permesso del microfono è stato negato o bloccato. Abilita il microfono per questo sito; fuori da localhost usa HTTPS se possibile.',
      speechNetwork: 'Il riconoscimento vocale non riesce a raggiungere la rete. Controlla connessione e firewall aziendale.',
      speechStartFailed: 'Impossibile avviare il riconoscimento vocale. Un’altra scheda potrebbe usare il microfono.',
      typingPlaceholder: 'Scrivi qualcosa all’assistente...',
      micTitle: 'Dettatura vocale - parla e il testo appare in tempo reale. Clicca di nuovo per fermare.',
      micAria: 'Dettatura vocale',
      processing: 'Risposta in corso...',
      fallbackResponse: 'Mi dispiace, al momento non posso rispondere.',
      suggestedAction: 'Azione suggerita',
      quickHelp: 'Spiega questa schermata',
      quickSupport: 'Apri ticket di supporto',
      quickReport: 'Prepara bozza report',
      quickAutomation: 'Prepara bozza automazione',
      confirmAction: 'Confermi questa azione?',
      actionSuccess: 'Azione completata.',
      actionFailed: 'Impossibile completare l’azione.',
      actionNeedsDraft: 'L’AI non ha fornito dati di bozza per questa azione. Aggiungi più dettagli e riprova.',
      unavailable: 'Il servizio assistente non è attualmente disponibile.',
      speechReadOnTitle: 'Lettura vocale attiva - disattiva',
      speechReadOffTitle: 'Lettura vocale disattivata - attiva',
      speechReadOnAria: 'Disattiva lettura vocale',
      speechReadOffAria: 'Attiva lettura vocale',
    },
  },
  ru: {
    voiceLocale: 'ru-RU',
    texts: {
      title: 'Рабочий ассистент QubePro',
      welcome:
        'Здравствуйте! Я ваш рабочий ассистент QubePro. Чем могу помочь? Вы можете писать или использовать голосовые команды.',
      unsupported: 'Ваш браузер не поддерживает голосовой ввод. Используйте Chrome, Edge или Safari.',
      micDenied:
        'Доступ к микрофону отклонен или заблокирован. Разрешите микрофон для этого сайта; вне localhost по возможности используйте HTTPS.',
      speechNetwork: 'Сервис распознавания речи недоступен по сети. Проверьте интернет-соединение и корпоративный firewall.',
      speechStartFailed: 'Не удалось запустить распознавание речи. Возможно, микрофон используется другой вкладкой.',
      typingPlaceholder: 'Напишите ассистенту...',
      micTitle: 'Голосовой ввод - говорите, и текст появится сразу. Нажмите еще раз, чтобы остановить.',
      micAria: 'Голосовой ввод',
      processing: 'Формирую ответ...',
      fallbackResponse: 'Извините, сейчас я не могу ответить.',
      suggestedAction: 'Предлагаемое действие',
      quickHelp: 'Объяснить этот экран',
      quickSupport: 'Открыть заявку в поддержку',
      quickReport: 'Подготовить черновик отчета',
      quickAutomation: 'Подготовить черновик автоматизации',
      confirmAction: 'Подтверждаете это действие?',
      actionSuccess: 'Действие выполнено.',
      actionFailed: 'Не удалось выполнить действие.',
      actionNeedsDraft: 'ИИ не передал черновые данные для этого действия. Добавьте больше деталей и попробуйте снова.',
      unavailable: 'Сервис ассистента сейчас недоступен.',
      speechReadOnTitle: 'Озвучивание включено - выключить',
      speechReadOffTitle: 'Озвучивание выключено - включить',
      speechReadOnAria: 'Выключить озвучивание',
      speechReadOffAria: 'Включить озвучивание',
    },
  },
  zh: {
    voiceLocale: 'zh-CN',
    texts: {
      title: 'QubePro 工作助手',
      welcome: '您好！我是您的 QubePro 工作助手。有什么可以帮您？您可以输入文字或使用语音指令。',
      unsupported: '您的浏览器不支持语音输入。请使用 Chrome、Edge 或 Safari。',
      micDenied: '麦克风权限被拒绝或阻止。请为此网站启用麦克风；在 localhost 之外请尽量使用 HTTPS。',
      speechNetwork: '语音识别无法连接网络。请检查互联网连接和企业防火墙。',
      speechStartFailed: '无法启动语音识别。可能有其他标签页正在使用麦克风。',
      typingPlaceholder: '给助手输入内容...',
      micTitle: '语音输入 - 说话后文本会实时显示。再次点击可停止。',
      micAria: '语音输入',
      processing: '正在回答...',
      fallbackResponse: '抱歉，我现在无法回答。',
      suggestedAction: '建议操作',
      quickHelp: '说明当前页面',
      quickSupport: '创建支持工单',
      quickReport: '准备报表草稿',
      quickAutomation: '准备自动化草稿',
      confirmAction: '确认执行此操作吗？',
      actionSuccess: '操作已完成。',
      actionFailed: '操作未能完成。',
      actionNeedsDraft: 'AI 未提供此操作所需的草稿数据。请补充更多细节后重试。',
      unavailable: '助手服务当前不可用。',
      speechReadOnTitle: '语音朗读已开启 - 关闭',
      speechReadOffTitle: '语音朗读已关闭 - 开启',
      speechReadOnAria: '关闭语音朗读',
      speechReadOffAria: '开启语音朗读',
    },
  },
  ja: {
    voiceLocale: 'ja-JP',
    texts: {
      title: 'QubePro ワークアシスタント',
      welcome: 'こんにちは。QubePro ワークアシスタントです。どのようにお手伝いできますか？文字入力または音声コマンドを使えます。',
      unsupported: 'このブラウザは音声入力をサポートしていません。Chrome、Edge、Safari を使用してください。',
      micDenied: 'マイクの許可が拒否またはブロックされました。このサイトでマイクを有効にし、localhost 以外では可能なら HTTPS を使用してください。',
      speechNetwork: '音声認識がネットワークに接続できません。インターネット接続と社内ファイアウォールを確認してください。',
      speechStartFailed: '音声認識を開始できませんでした。別のタブがマイクを使用している可能性があります。',
      typingPlaceholder: 'アシスタントに入力...',
      micTitle: '音声入力 - 話すとテキストがリアルタイムで表示されます。もう一度クリックすると停止します。',
      micAria: '音声入力',
      processing: '回答中...',
      fallbackResponse: '申し訳ありません。現在回答できません。',
      suggestedAction: '推奨アクション',
      quickHelp: 'この画面を説明',
      quickSupport: 'サポートチケットを作成',
      quickReport: 'レポート下書きを準備',
      quickAutomation: '自動化下書きを準備',
      confirmAction: 'この操作を確認しますか？',
      actionSuccess: '操作が完了しました。',
      actionFailed: '操作を完了できませんでした。',
      actionNeedsDraft: 'この操作に必要な下書きデータが AI から返りませんでした。もう少し詳しく入力して再試行してください。',
      unavailable: '現在、アシスタントサービスを利用できません。',
      speechReadOnTitle: '音声読み上げはオンです - オフにする',
      speechReadOffTitle: '音声読み上げはオフです - オンにする',
      speechReadOnAria: '音声読み上げをオフにする',
      speechReadOffAria: '音声読み上げをオンにする',
    },
  },
};

function resolveAssistantLanguageCode(language?: string): AssistantLanguageCode {
  const normalized = normalizeAssistantLanguage(language).split('-')[0];
  if (normalized === 'en' || normalized === 'ar' || normalized === 'de' || normalized === 'fr' || normalized === 'es') {
    return normalized;
  }
  if (normalized === 'it' || normalized === 'ru' || normalized === 'zh' || normalized === 'ja') return normalized;
  return 'tr';
}

/** Web Speech API — user language first, then html/i18next/navigator fallback. */
function resolveVoiceLocale(language?: string): string {
  return ASSISTANT_LANGUAGE_CONFIG[resolveAssistantLanguageCode(language)].voiceLocale;
}

function resolveAssistantTexts(language?: string): AssistantTexts {
  return ASSISTANT_LANGUAGE_CONFIG[resolveAssistantLanguageCode(language)].texts;
}

const SPEECH_READ_STORAGE_KEY = 'qubepro_global_assistant_speech_read_enabled';

function readInitialSpeechReadEnabled(): boolean {
  try {
    const stored = localStorage.getItem(SPEECH_READ_STORAGE_KEY);
    if (stored === 'false') return false;
  } catch {
    /* private mode */
  }
  return true;
}

type Message = {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp?: string;
  actions?: AssistantSuggestedAction[];
  proposalReceiptId?: string;
  dataView?: AssistantDataView;
  dataPresentation?: AssistantDataPresentation;
  verifiedAnalysis?: AssistantVerifiedAnalysis;
  sources?: AssistantDataSource[];
  assistant?: AssistantIdentity | null;
  targetAssistant?: AssistantIdentity | null;
  routingMode?: AssistantWorkMode;
  expertContributions?: AssistantExpertContribution[];
};

type AssistantExpertContribution = {
  assistant: AssistantIdentity;
  status: 'COMPLETED' | 'NEEDS_CLARIFICATION' | 'FAILED';
  question?: string;
  answer?: string;
  sources: AssistantDataSource[];
  limitations: string[];
};

type AssistantPickerPurpose = 'MENTION' | 'PINNED' | 'BOARD';
type AssistantTargetRetention = 'ONE_SHOT' | 'PINNED';

const SUPPORT_CONTEXT_ACTION_TYPES = new Set<AssistantActionType>([
  'focus_support_description',
  'capture_screenshot',
  'open_support_ticket',
  'support_main_menu',
]);

type AssistantConversationSummary = {
  purpose?: AssistantConversationPurpose | null;
  id: number | string;
  assistantId?: number | string;
  assistant?: AssistantIdentity | null;
  title?: string;
  context?: Record<string, unknown> | null;
  tokenCount?: number | null;
  createdAt?: string;
  updatedAt?: string;
  archivedAt?: string | null;
};

type AssistantHistoryProductScope = 'all' | 'current';

export type GlobalVoiceAssistantContext = {
  pageContext?: Record<string, unknown>;
  userContext?: Record<string, unknown>;
  companyContext?: Record<string, unknown>;
  uiContext?: Record<string, unknown>;
};

export type GlobalVoiceAssistantProps = {
  /** Sıfırdan büyüdükçe (Layout’tan artırılır) sesli asistan kabuğu + panel açılır */
  activationNonce?: number;
  /** Sohbet paneli açık/kapalı (header `active` senkronu) */
  onPanelOpenChange?: (open: boolean) => void;
  onPanelExpandedChange?: (expanded: boolean) => void;
  /** >0 arttıkça oturum açıkken paneli tekrar aç (header ikinci tık) */
  panelExpandKey?: number;
  /** Varsayılan: /api/assistant (ürün API'sinde server-token proxy) */
  apiBasePath?: string;
  applicationCode?: string;
  language?: string;
  /** Seçili iş firması. Yalnız pozitif kimlik X-Company-Id olarak gönderilir. */
  companyId?: string | number | null;
  contextProvider?: () => GlobalVoiceAssistantContext;
};

function buildAssistantRequestUrl(apiBasePath: string, path: string): string {
  const base = (apiBasePath || '/api/assistant').replace(/\/+$/, '');
  const normalizedBase = /^https?:\/\//i.test(base) || base.startsWith('/') ? base : `/${base}`;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = `${normalizedBase}${suffix}`;
  return new URL(withViteBasePathForApiDms(url), window.location.origin).toString();
}

function buildAiAppBaseUrl(apiBasePath: string): string {
  const base = (apiBasePath || '/api/assistant').trim().replace(/\/+$/, '');
  const env = import.meta.env as unknown as {
    VITE_AI_APP_BASE_URL?: string;
    VITE_GLOBAL_ASSISTANT_APP_BASE_URL?: string;
    VITE_PLATFORM_URL?: string;
    VITE_BASE_URL?: string;
  };
  const explicitAiAppBase = String(
    env.VITE_AI_APP_BASE_URL || env.VITE_GLOBAL_ASSISTANT_APP_BASE_URL || '',
  ).trim().replace(/\/+$/, '');
  if (explicitAiAppBase) {
    const normalizedExplicit =
      /^https?:\/\//i.test(explicitAiAppBase) || explicitAiAppBase.startsWith('/')
        ? explicitAiAppBase
        : `/${explicitAiAppBase}`;
    return new URL(normalizedExplicit, window.location.origin).toString().replace(/\/+$/, '');
  }
  const normalizedApiBase = base.replace(/^\/+/, '').toLowerCase();
  if (normalizedApiBase === 'api/assistant' || normalizedApiBase.startsWith('api/assistant/')) {
    const platformBase = String(env.VITE_PLATFORM_URL || env.VITE_BASE_URL || '').trim().replace(/\/+$/, '');
    const aiBaseFromPlatform = platformBase ? `${platformBase}/ai` : '/ai';
    return new URL(aiBaseFromPlatform, window.location.origin).toString().replace(/\/+$/, '');
  }
  const aiBase = (base.replace(/\/api\/assistant$/i, '').replace(/\/api$/i, '') || '/ai').replace(/\/+$/, '');
  const normalizedBase = /^https?:\/\//i.test(aiBase) || aiBase.startsWith('/') ? aiBase : `/${aiBase}`;
  return new URL(normalizedBase, window.location.origin).toString().replace(/\/+$/, '');
}

function buildAiAppUrl(apiBasePath: string, path: string): string {
  const base = buildAiAppBaseUrl(apiBasePath);
  const suffix = path.replace(/^\/+/, '');
  return suffix ? `${base}/${suffix}` : base;
}

function getDefaultApplicationCode(): string {
  const env = import.meta.env as unknown as { VITE_APPLICATION_CODE?: string; VITE_APP_CODE?: string };
  return (env.VITE_APPLICATION_CODE || env.VITE_APP_CODE || '').trim() || 'CORE';
}

function resolveAssistantLanguage(language?: string): string {
  return resolveAssistantLanguageCode(language);
}

function buildQubeProDocsUrl(appCode: string, language?: string, pagePath?: string): string {
  const docsLanguage = resolveAssistantLanguageCode(language);
  const product = String(appCode || 'CORE').trim().toLowerCase() || 'core';
  const env = import.meta.env as unknown as { VITE_DOCS_BASE_URL?: string };
  const docsRoot = (env.VITE_DOCS_BASE_URL || '/docs').trim().replace(/\/+$/, '');
  const baseUrl = `${docsRoot}/${docsLanguage}/${product}`;
  const slug = String(pagePath || '')
    .trim()
    .replace(/[?#].*$/, '')
    .replace(/^\/+|\/+$/g, '');
  return slug ? `${baseUrl}/${slug}` : baseUrl;
}

function normalizeAssistantCompanyId(value: unknown): string {
  const text = String(value ?? '').trim();
  return /^[1-9]\d*$/.test(text) ? text : '';
}

function buildAssistantHeaders(companyId?: string | number | null): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  const scopedCompanyId = normalizeAssistantCompanyId(companyId);
  if (scopedCompanyId) headers['X-Company-Id'] = scopedCompanyId;
  const authHeader = axios.defaults.headers.common['Authorization'];
  if (typeof authHeader === 'string' && authHeader) {
    headers.Authorization = authHeader;
  }
  return headers;
}

function buildAssistantApiRequestUrl(apiBasePath: string, path: string): string {
  const base = (apiBasePath || '/api/assistant').replace(/\/+$/, '');
  const apiRoot = (base.replace(/\/assistant$/i, '') || '/api').replace(/\/+$/, '');
  const normalizedBase = /^https?:\/\//i.test(apiRoot) || apiRoot.startsWith('/') ? apiRoot : `/${apiRoot}`;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = `${normalizedBase}${suffix}`;
  return new URL(withViteBasePathForApiDms(url), window.location.origin).toString();
}

function readTokenFromPayload(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const record = payload as Record<string, any>;
  const data = record.data && typeof record.data === 'object' ? record.data as Record<string, any> : null;
  return String(record.token || record.accessToken || data?.token || data?.accessToken || '').trim();
}

async function parseJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function readAssistantError(response: Response): Promise<AssistantWorkResponseError & { supportFailure: SupportSubmissionFailure }> {
  const payload = await parseJsonSafe(response) as Record<string, any> | null;
  const data = payload?.data && typeof payload.data === 'object' ? payload.data as Record<string, any> : null;
  const message = payload?.error || payload?.message || data?.error || data?.message || response.statusText;
  return Object.assign(new AssistantWorkResponseError(
    typeof message === 'string' && message.trim() ? message.trim() : `Asistan servisi HTTP ${response.status} döndü.`, response.status,
  ), { supportFailure: readSupportSubmissionFailure(response.status, payload) });
}

async function retryAssistantChatWithToken(
  chatUrl: string,
  requestInit: RequestInit,
  token: string,
): Promise<Response | null> {
  const cleanedToken = token.replace(/^Bearer\s+/i, '').trim();
  if (!cleanedToken) return null;
  const headers = { ...(requestInit.headers as Record<string, string>), Authorization: `Bearer ${cleanedToken}` };
  const retryResponse = await fetch(chatUrl, { ...requestInit, headers });
  return retryResponse.ok ? retryResponse : retryResponse.status === 401 ? null : retryResponse;
}

async function recoverAssistantChatRequest(
  apiBasePath: string,
  chatUrl: string,
  requestInit: RequestInit,
): Promise<Response | null> {
  try {
    const refreshRes = await fetch(buildAssistantApiRequestUrl(apiBasePath, '/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: requestInit.signal,
    });
    if (refreshRes.ok) {
      const token = readTokenFromPayload(await parseJsonSafe(refreshRes));
      const recovered = token
        ? await retryAssistantChatWithToken(chatUrl, requestInit, token)
        : await fetch(chatUrl, requestInit);
      if (recovered) return recovered;
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    /* asıl 401 korunacak */
  }

  return null;
}

async function sendAssistantRequest(
  apiBasePath: string,
  path: string,
  options: {
    method?: string;
    body?: Record<string, unknown>;
    companyId?: string | number | null;
    signal?: AbortSignal;
    idempotencyKey?: string;
  } = {},
): Promise<unknown> {
  const requestUrl = buildAssistantRequestUrl(apiBasePath, path);
  const requestInit: RequestInit = {
    method: options.method || 'GET',
    credentials: 'include',
    headers: { ...buildAssistantHeaders(options.companyId), ...(options.idempotencyKey ? { 'Idempotency-Key': options.idempotencyKey } : {}) },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  let response = await fetch(requestUrl, requestInit);
  if (response.status === 401) {
    response = await recoverAssistantChatRequest(apiBasePath, requestUrl, requestInit) || response;
  }

  if (!response.ok) {
    throw await readAssistantError(response);
  }

  return parseJsonSafe(response);
}

async function sendAssistantPostRequest(
  apiBasePath: string,
  path: string,
  body: Record<string, unknown>,
  companyId?: string | number | null,
  signal?: AbortSignal,
): Promise<unknown> {
  return sendAssistantRequest(apiBasePath, path, { method: 'POST', body, companyId, signal });
}

async function sendAssistantChatRequest(
  apiBasePath: string,
  body: Record<string, unknown>,
  companyId?: string | number | null,
  signal?: AbortSignal,
): Promise<unknown> {
  const purpose = readAssistantPurpose(body.purpose);
  if (!purpose) throw new Error('Assistant purpose is required');
  return sendAssistantPostRequest(apiBasePath, assistantPurposePath('/chat', purpose), buildAssistantPurposeChatPayload(purpose, body), companyId, signal);
}

function isObjectRecord(value: unknown): value is Record<string, any> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function normalizeSupportTarget(value: unknown): SupportTarget | null {
  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return normalized === 'QUBEPRO_SAAS' || normalized === 'TENANT_INTERNAL'
    ? normalized
    : null;
}

function unwrapAssistantPayload(payload: unknown): Record<string, any> {
  if (!isObjectRecord(payload)) return {};
  return isObjectRecord(payload.data) ? payload.data : payload;
}

function buildAssistantUploadHeaders(companyId?: string | number | null): Record<string, string> {
  const headers = buildAssistantHeaders(companyId);
  delete headers['Content-Type'];
  return headers;
}

async function uploadAssistantAttachment(
  apiBasePath: string,
  file: File,
  supportTarget: SupportTarget,
  id = createAssistantId('file'),
  companyId?: string | number | null,
): Promise<AssistantAttachment> {
  const form = new FormData();
  form.append('id', id);
  form.append('supportTarget', supportTarget);
  form.append('file', file, file.name);

  const response = await fetch(buildAssistantRequestUrl(apiBasePath, '/attachments'), {
    method: 'POST',
    credentials: 'include',
    headers: buildAssistantUploadHeaders(companyId),
    body: form,
  });

  if (!response.ok) {
    throw await readAssistantError(response);
  }

  const data = unwrapAssistantPayload(await parseJsonSafe(response));
  const dmsFileRef = typeof data.dmsFileRef === 'string' ? data.dmsFileRef.trim() : '';
  if (!dmsFileRef.startsWith('dms:FILE:')) {
    throw new Error('DMS dosya referansÄ± alÄ±namadÄ±.');
  }

  return {
    id: typeof data.id === 'string' && data.id.trim() ? data.id.trim() : id,
    name: typeof data.name === 'string' && data.name.trim() ? data.name.trim() : file.name,
    type: typeof data.type === 'string' && data.type.trim() ? data.type.trim() : (file.type || 'application/octet-stream'),
    size: typeof data.size === 'number' && Number.isFinite(data.size) ? data.size : file.size,
    dmsFileRef,
    supportTarget: normalizeSupportTarget(data.supportTarget) || supportTarget,
  };
}

type GlobalAssistantConversationMode = 'chat' | 'support';

type GlobalAssistantConversationEventRecord = {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  metadata?: Record<string, unknown>;
};

function inferGlobalAssistantConversationMode(events: GlobalAssistantConversationEventRecord[]): GlobalAssistantConversationMode {
  return events.some((event) => {
    const metadata = isObjectRecord(event.metadata) ? event.metadata : {};
    const handledBy = String(metadata.handledBy || '').toLowerCase();
    const action = String(metadata.action || metadata.actionType || '').toLowerCase();
    const intent = String(metadata.intent || '').toLowerCase();
    return handledBy.includes('support') ||
      action.includes('support') ||
      intent.includes('support') ||
      Boolean(metadata.supportTicketDraft);
  }) ? 'support' : 'chat';
}

function notifyGlobalAssistantConversationUpdated(detail: {
  conversationId?: number | string | null;
  assistantId?: number | string | null;
  applicationCode?: string;
  mode: GlobalAssistantConversationMode;
}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GLOBAL_ASSISTANT_CONVERSATION_EVENT, {
    detail: {
      source: 'global-voice-assistant',
      updatedAt: Date.now(),
      ...detail,
    },
  }));
}

function stripAssistantJsonFence(value: string): string {
  return value
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
}

function readAssistantDisplayText(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const text = value.trim();
  if (!text) return fallback;

  const candidates = [stripAssistantJsonFence(text)];
  const firstBrace = candidates[0].indexOf('{');
  const lastBrace = candidates[0].lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    candidates.push(candidates[0].slice(firstBrace, lastBrace + 1));
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (isObjectRecord(parsed)) {
        const answer = parsed.answer ?? parsed.response ?? parsed.message?.content;
        if (typeof answer === 'string' && answer.trim()) return answer.trim();
      }
    } catch {
      // Not JSON; keep the original text.
    }
  }

  return text;
}

function normalizeSuggestedActions(value: unknown): AssistantSuggestedAction[] {
  return normalizeAssistantSuggestedActions(value, 'İşlem');
}

function normalizeConversationItems(value: unknown): AssistantConversationSummary[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isObjectRecord)
    .map((item) => ({
      id: item.id,
      purpose: readAssistantPurpose(item.purpose),
      assistantId: item.assistantId,
      assistant: isObjectRecord(item.assistant) ? normalizeAssistantIdentity(item.assistant) : null,
      title: typeof item.title === 'string' ? item.title : undefined,
      context: isObjectRecord(item.context) ? item.context : null,
      tokenCount: typeof item.tokenCount === 'number' ? item.tokenCount : null,
      createdAt: typeof item.createdAt === 'string' ? item.createdAt : undefined,
      updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : undefined,
      archivedAt: typeof item.archivedAt === 'string' ? item.archivedAt : null,
    }))
    .filter((item) => item.id !== undefined && item.id !== null);
}

function normalizeAssistantIdentity(value: unknown): AssistantIdentity | null {
  if (!isObjectRecord(value)) return null;
  const id = value.id ?? value.Id;
  const name = typeof value.name === 'string' ? value.name.trim() : '';
  if ((typeof id !== 'number' && typeof id !== 'string') || !name) return null;
  return {
    id,
    name,
    description: typeof value.description === 'string' ? value.description : null,
    isSystem: value.isSystem === true,
    isDefault: value.isDefault === true,
    isFavorite: value.isFavorite === true,
    kind: typeof value.kind === 'string' ? value.kind : undefined,
    readiness: typeof value.readiness === 'string' ? value.readiness : undefined,
  };
}

function normalizeAssistantIdentities(payload: unknown): AssistantIdentity[] {
  const raw = isObjectRecord(payload) && Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  return raw.map(normalizeAssistantIdentity).filter((item): item is AssistantIdentity => Boolean(item));
}

function normalizeAssistantWorkMode(value: unknown): AssistantWorkMode | undefined {
  return value === 'AUTO'
    || value === 'SINGLE_EXPERT'
    || value === 'EXPERT_BOARD'
    || value === 'EXPERT_360'
    ? value
    : undefined;
}

function normalizeAssistantExpertContributions(value: unknown): AssistantExpertContribution[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isObjectRecord(item)) return [];
    const assistant = normalizeAssistantIdentity(item.assistant);
    const status = item.status === 'COMPLETED' || item.status === 'NEEDS_CLARIFICATION' || item.status === 'FAILED'
      ? item.status
      : null;
    if (!assistant || !status) return [];
    return [{
      assistant,
      status,
      question: typeof item.question === 'string' ? item.question : undefined,
      answer: typeof item.answer === 'string' ? item.answer : undefined,
      sources: normalizeAssistantDataSources(item.sources),
      limitations: Array.isArray(item.limitations)
        ? item.limitations.filter((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()))
        : [],
    }];
  });
}

function normalizeConversationMessages(value: unknown, fallbackWelcome: string): Message[] {
  if (!Array.isArray(value)) {
    return [{ id: '1', role: 'ai', content: fallbackWelcome, timestamp: createAssistantMessageTimestamp() }];
  }
  const items = value
    .filter(isObjectRecord)
    .map((item, index) => {
      const content = typeof item.content === 'string' ? item.content.trim() : '';
      const role = item.role === 'user' ? 'user' : 'ai';
      if (!content) return null;
      const timestamp = normalizeAssistantMessageTimestamp(item.timestamp)
        ?? normalizeAssistantMessageTimestamp(item.createdAt);
      const metadata = isObjectRecord(item.metadata) ? item.metadata : {};
      const dataView = normalizeAssistantDataView(item.dataView ?? metadata.dataView);
      const verifiedAnalysis = normalizeAssistantVerifiedAnalysis(
        item.verifiedAnalysis ?? metadata.verifiedAnalysis,
      );
      const sources = normalizeAssistantDataSources(item.sources ?? metadata.sources);
      const actions = normalizeSuggestedActions(item.suggestedActions ?? metadata.suggestedActions);
      const assistant = normalizeAssistantIdentity(item.assistant ?? metadata.assistant ?? metadata.respondedBy);
      const targetAssistant = normalizeAssistantIdentity(item.targetAssistant ?? metadata.targetAssistant);
      const routingMode = normalizeAssistantWorkMode(item.routingMode ?? metadata.routingMode);
      const expertContributions = normalizeAssistantExpertContributions(
        item.expertContributions ?? metadata.expertContributions,
      );
      return {
        id: `history-${index}-${timestamp ?? 'undated'}`,
        role,
        content,
        ...(timestamp ? { timestamp } : {}),
        ...(actions.length > 0 ? { actions } : {}),
        proposalReceiptId: readAssistantProductProposalReceipt(metadata),
        ...(dataView ? { dataView } : {}),
        ...(dataView ? {
          dataPresentation: normalizeAssistantDataPresentation(
            item.dataPresentation ?? metadata.dataPresentation,
          ),
        } : {}),
        ...(verifiedAnalysis ? { verifiedAnalysis } : {}),
        ...(sources.length > 0 ? { sources } : {}),
        ...(assistant ? { assistant } : {}),
        ...(targetAssistant ? { targetAssistant } : {}),
        ...(routingMode ? { routingMode } : {}),
        ...(expertContributions.length > 0 ? { expertContributions } : {}),
      } as Message;
    })
    .filter((item): item is Message => Boolean(item));
  return items.length
    ? items
    : [{ id: '1', role: 'ai', content: fallbackWelcome, timestamp: createAssistantMessageTimestamp() }];
}

function getAssistantActionDraft(action: AssistantSuggestedAction): Record<string, unknown> | null {
  const payload = isObjectRecord(action.payload) ? action.payload : {};
  const rawDraft =
    action.type === 'open_support_ticket'
      ? payload.draft ?? payload.supportTicketDraft ?? payload.ticketDraft
      : action.type === 'create_report_draft'
        ? payload.reportDraft ?? payload.draft
        : action.type === 'create_automation_draft'
          ? payload.automationDraft ?? payload.draft
          : null;
  return isObjectRecord(rawDraft) ? rawDraft : null;
}

function normalizeCommandText(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/[çğıöşü]/g, (char) => ({
      ç: 'c',
      ğ: 'g',
      ı: 'i',
      ö: 'o',
      ş: 's',
      ü: 'u',
    }[char] || char))
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isSupportOpenCommand(value: string, quickSupportLabel: string): boolean {
  const text = normalizeCommandText(value);
  const quick = normalizeCommandText(quickSupportLabel);
  if (!text) return false;
  if (text === quick) return true;
  return /(^|\s)(destek kaydi ac|destek kaydi olustur|destek kaydi baslat|destek talebi olustur|destek ac|destek olustur|ticket ac|ticket olustur|support ticket|open support ticket|create support ticket)(\s|$)/.test(text);
}

function isSupportSendCommand(value: string): boolean {
  const text = normalizeCommandText(value);
  return [
    'gonder',
    'kaydi gonder',
    'destek kaydini gonder',
    'ticket gonder',
    'send',
    'send ticket',
  ].includes(text);
}

function isSupportMainMenuCommand(value: string): boolean {
  const text = normalizeCommandText(value);
  return [
    'ana menu',
    'ana menuye git',
    'menu',
    'main menu',
    'back',
  ].includes(text);
}

function stripSupportCommand(value: string, quickSupportLabel: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const normalized = normalizeCommandText(trimmed);
  if (normalized === normalizeCommandText(quickSupportLabel)) return '';
  if (!isSupportOpenCommand(trimmed, quickSupportLabel)) return trimmed;
  if (/^(destek kaydi ac|destek kaydi olustur|destek kaydi baslat|destek talebi olustur|destek ac|destek olustur|ticket ac|ticket olustur|support ticket|open support ticket|create support ticket)$/.test(normalized)) {
    return '';
  }

  return trimmed
    .replace(/^\s*(destek\s+kayd[ıi]\s+a[cç]|destek\s+a[cç]|ticket\s+a[cç]|support\s+ticket|open\s+support\s+ticket)\s*[:,-]?\s*/i, '')
    .trim();
}

function hasMeaningfulSupportIssue(value: string): boolean {
  const text = value.trim();
  if (text.length < 8) return false;
  return !isSupportSendCommand(text) && !isSupportMainMenuCommand(text);
}

function getVisibleTextSnapshot(): string {
  const target = document.querySelector<HTMLElement>('[data-qp-main-content="true"], .main-content') ?? document.body;
  const text = (target?.innerText || '')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 3000 ? `${text.slice(0, 3000)}...[kırpıldı]` : text;
}

function describeActiveElement(): Record<string, string> | null {
  const el = document.activeElement;
  if (!el || el === document.body) return null;
  const node = el as HTMLElement;
  return {
    tag: node.tagName.toLowerCase(),
    id: node.id || '',
    name: node.getAttribute('name') || '',
    label: node.getAttribute('aria-label') || node.getAttribute('title') || '',
    text: (node.innerText || node.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 200),
  };
}

const SENSITIVE_QUERY_PATTERN = /token|secret|key|password|parola|sifre|şifre|authorization|cookie|credential/i;

function sanitizeSearch(search: string): string {
  if (!search) return '';
  try {
    const params = new URLSearchParams(search);
    params.forEach((_value, key) => {
      if (SENSITIVE_QUERY_PATTERN.test(key)) params.set(key, '[masked]');
    });
    const text = params.toString();
    return text ? `?${text}` : '';
  } catch {
    return '';
  }
}

function sanitizeHash(hash: string): string {
  if (!hash) return '';
  return SENSITIVE_QUERY_PATTERN.test(hash) ? '#[masked]' : hash.slice(0, 300);
}

function sanitizeCurrentUrl(): string {
  try {
    const url = new URL(window.location.href);
    url.search = sanitizeSearch(url.search);
    url.hash = sanitizeHash(url.hash);
    return url.toString();
  } catch {
    return '';
  }
}

function buildScreenSnapshotContext(): Record<string, unknown> {
  const safeSearch = sanitizeSearch(window.location.search);
  const safeHash = sanitizeHash(window.location.hash);
  return {
    capturedAt: new Date().toISOString(),
    url: sanitizeCurrentUrl(),
    path: window.location.pathname,
    search: safeSearch,
    hash: safeHash,
    title: document.title,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    selectedText: window.getSelection()?.toString().trim().slice(0, 1000) || '',
    activeElement: describeActiveElement(),
    visibleText: getVisibleTextSnapshot(),
  };
}

export default function GlobalVoiceAssistant(props: GlobalVoiceAssistantProps) {
  const { user } = useAuth();
  return <ScopedGlobalVoiceAssistant key={JSON.stringify([user?.id, user?.tenantId, props.companyId])} {...props} />;
}

function ScopedGlobalVoiceAssistant({
  activationNonce = 0,
  onPanelOpenChange,
  onPanelExpandedChange,
  panelExpandKey = 0,
  apiBasePath = '/api/assistant',
  applicationCode,
  language,
  companyId,
  contextProvider,
}: GlobalVoiceAssistantProps) {
  const { user: formActor } = useAuth();
  const formAuthorityRef = useRef('');
  formAuthorityRef.current = JSON.stringify([formActor?.id, formActor?.tenantId, formActor?.membershipId, formActor?.authVersion, formActor?.membershipVersion, formActor?.isAdmin, formActor?.permissions]);
  const texts = resolveAssistantTexts(language);
  const purposeTexts = assistantPurposeCopy(language);
  const dateTime = useDateTime();
  const voiceLocale = resolveVoiceLocale(language);
  const feedbackLanguage = resolveAssistantLanguageCode(language) as SupportedLanguage;
  const initialPanelOpenRef = useRef(readAiPanelPref(LS_AI_PANEL_OPEN));
  const [engaged, setEngaged] = useState(() => initialPanelOpenRef.current);
  const [isOpen, setIsOpen] = useState(() => initialPanelOpenRef.current);
  /** Genişlik oturumluk bir çalışma alanıdır; F5 her zaman kurumsal varsayılana döner. */
  const [isExpanded, setIsExpanded] = useState(false);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  /** Yalnız panelin açık/kapalı tercihi kalıcıdır; genişlik ve genişletme geçicidir. */
  useEffect(() => {
    document.body.classList.remove('qp-gva-docked');
    document.body.style.removeProperty('--qp-gva-dock-width');
    writeAiPanelPref(LS_AI_PANEL_OPEN, isOpen);
    cleanupLegacyExpandedPanelPref();
  }, [isOpen]);

  const [purpose, setPurpose] = useState<AssistantConversationPurpose>(() => readAssistantPurpose(readAiStringPref(LS_AI_CONVERSATION_PURPOSE)) ?? 'BUSINESS');
  const purposeRef = useRef(purpose);
  const historyRequestIdRef = useRef(0);
  useEffect(() => { writeAiStringPref(LS_AI_CONVERSATION_PURPOSE, purpose); }, [purpose]);
  const [isListening, setIsListening] = useState(false);
  const [conversationId, setConversationId] = useState<number | string | null>(null);
  const [messages, setMessages] = useState<Message[]>(() => [
    {
      id: '1',
      role: 'ai',
      content: purposeRef.current === 'QUBEPRO_HELP' ? purposeTexts.helpHint : texts.welcome,
      timestamp: createAssistantMessageTimestamp(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [composerMenuOpen, setComposerMenuOpen] = useState(false);
  const [isProcessingState, setIsProcessing] = useState(false);
  const [isChatRequestActive, setIsChatRequestActive] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const productFormRequestRef = useRef<AbortController | null>(null);
  const [draftReview, setDraftReview] = useState<{ action: AssistantDraftAction; scope: number; conversationId: number | string | null; error?: string } | null>(null);
  const [attachments, setAttachments] = useState<AssistantAttachment[]>([]);
  const [supportDescriptionMode, setSupportDescriptionMode] = useState(false);
  const [pendingSupportDraft, setPendingSupportDraft] = useState<Record<string, unknown> | null>(null);
  // Ortak destek formu (AiChat ile aynı bileşen): destek modunda panelde gösterilir.
  const [supportForm, setSupportForm] = useState<SupportFormState>(() => ({ ...SUPPORT_FORM_INITIAL, supportTarget: supportTargetForPurpose(purpose) }));
  const [supportFormFailure, setSupportFormFailure] = useState<SupportSubmissionFailure | null>(null);
  const [isCapturingScreen, setIsCapturingScreen] = useState(false);
  const captureInProgressRef = useRef(false);
  const [localScreenshot, setLocalScreenshot] = useState<{ file: File; url: string; scope: number } | null>(null);
  const [assistantMenuOpen, setAssistantMenuOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<AssistantIdentity | null>(null);
  const [availableAssistants, setAvailableAssistants] = useState<AssistantIdentity[]>([]);
  const [assistantPickerOpen, setAssistantPickerOpen] = useState(false);
  const [assistantPickerQuery, setAssistantPickerQuery] = useState('');
  const [assistantPickerQueryRevision, setAssistantPickerQueryRevision] = useState(0);
  const [assistantPickerLoading, setAssistantPickerLoading] = useState(false);
  const [assistantPickerError, setAssistantPickerError] = useState('');
  const [assistantPickerPurpose, setAssistantPickerPurpose] = useState<AssistantPickerPurpose>('MENTION');
  const [workMode, setWorkMode] = useState<AssistantWorkMode>('AUTO');
  const [workModeMenuOpen, setWorkModeMenuOpen] = useState(false);
  const [targetAssistant, setTargetAssistant] = useState<AssistantIdentity | null>(null);
  const [targetRetention, setTargetRetention] = useState<AssistantTargetRetention>('ONE_SHOT');
  const [composerMentions, setComposerMentions] = useState<AssistantComposerMention[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState<AssistantConversationSummary[]>([]);
  const [historyError, setHistoryError] = useState('');
  const [showArchivedConversations, setShowArchivedConversations] = useState(false);
  const [historyProductScope, setHistoryProductScope] = useState<AssistantHistoryProductScope>('all');
  const [speechReadEnabled, setSpeechReadEnabled] = useState<boolean>(() => readInitialSpeechReadEnabled());
  const supportFlowActive = supportDescriptionMode || attachments.length > 0 || Boolean(pendingSupportDraft);
  const expertBoardPrompts = buildAssistantExpertPrompts(inputValue, composerMentions);
  const expertBoardReady = expertBoardPrompts.length >= 2
    && expertBoardPrompts.length <= 5
    && expertBoardPrompts.every((prompt) => Boolean(prompt.question.trim()));
  const conversationIdRef = useRef<number | string | null>(null);
  const selectedAssistantRef = useRef<AssistantIdentity | null>(null);
  const workModeRef = useRef<AssistantWorkMode>('AUTO');
  const targetAssistantRef = useRef<AssistantIdentity | null>(null);
  const targetRetentionRef = useRef<AssistantTargetRetention>('ONE_SHOT');
  const composerMentionsRef = useRef<AssistantComposerMention[]>([]);
  const chatRequestAbortControllerRef = useRef<AbortController | null>(null);
  const assistantPickerRequestIdRef = useRef(0);
  const assistantPickerLoadedScopeRef = useRef<string | null>(null);
  const supportScopeRef = useRef({ key: '', version: 0 });
  const supportScopeKey = `${normalizeAssistantCompanyId(companyId) ?? ''}:${selectedAssistant?.id ?? ''}:${supportForm.supportTarget ?? ''}`;
  useEffect(() => { setSupportFormFailure(null); }, [supportScopeKey, supportDescriptionMode]);
  useEffect(() => { setLocalScreenshot(null); }, [supportScopeKey]);
  useEffect(() => { if (!supportDescriptionMode) setLocalScreenshot(null); }, [supportDescriptionMode]);
  useEffect(() => () => { if (localScreenshot) URL.revokeObjectURL(localScreenshot.url); }, [localScreenshot]);
  if (supportScopeRef.current.key !== supportScopeKey) {
    supportScopeRef.current = { key: supportScopeKey, version: supportScopeRef.current.version + 1 };
  }


  useEffect(() => {
    conversationIdRef.current = conversationId;
    if (conversationId) {
      writeAiStringPref(LS_AI_ACTIVE_CONVERSATION_ID, conversationId);
      return;
    }
    if (restoredConversationLoadedRef.current || !restoredConversationIdRef.current) {
      writeAiStringPref(LS_AI_ACTIVE_CONVERSATION_ID, null);
    }
  }, [conversationId]);

  useEffect(() => {
    selectedAssistantRef.current = selectedAssistant;
  }, [selectedAssistant]);

  useEffect(() => {
    workModeRef.current = workMode;
  }, [workMode]);

  useEffect(() => {
    targetAssistantRef.current = targetAssistant;
  }, [targetAssistant]);

  useEffect(() => {
    targetRetentionRef.current = targetRetention;
  }, [targetRetention]);

  useEffect(() => {
    composerMentionsRef.current = composerMentions;
  }, [composerMentions]);

  useEffect(() => () => {
    supportScopeRef.current.version += 1;
    productFormRequestRef.current?.abort();
    chatRequestAbortControllerRef.current?.abort();
    chatRequestAbortControllerRef.current = null;
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLSpanElement>(null);
  const supportDescriptionRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    if (supportDescriptionMode && isOpen) supportDescriptionRef.current?.focus();
  }, [supportDescriptionMode, isOpen]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const footerActionsRef = useRef<HTMLDivElement>(null);
  const panelResizeSessionRef = useRef<GlobalAssistantPanelResizeSession | null>(null);
  const recognitionRef = useRef<any>(null);
  /** Mikrofon oturumu başlamadan önceki metin kutusu içeriği (ara + nihai transcript birleşimi). */
  const voiceDictationPrefixRef = useRef('');
  const restoredConversationIdRef = useRef(readAiStringPref(LS_AI_ACTIVE_CONVERSATION_ID));
  const restoredConversationLoadedRef = useRef(false);

  const work = useAssistantWork({
    scopeKey: `${purpose}:${selectedAssistant?.id ?? ''}:${supportScopeRef.current.version}`,
    conversationId,
    enabled: engaged && isOpen && purpose === 'BUSINESS',
    language,
    request: (path, options) => sendAssistantRequest(apiBasePath, path, { ...options, companyId }),
    onAccepted: (result) => {
      conversationIdRef.current = result.conversationId;
      setConversationId(result.conversationId);
      notifyGlobalAssistantConversationUpdated({ conversationId: result.conversationId,
        assistantId: selectedAssistantRef.current?.id, applicationCode, mode: 'chat' });
      if (targetAssistantRef.current && targetRetentionRef.current === 'ONE_SHOT') {
        targetAssistantRef.current = null; setTargetAssistant(null);
        workModeRef.current = 'AUTO'; setWorkMode('AUTO');
      }
    },
    onResults: async (id, signal) => {
      const version = supportScopeRef.current.version;
      const payload = await sendAssistantRequest(apiBasePath,
        assistantPurposePath(`/conversations/${id}`, 'BUSINESS'), { companyId, signal });
      if (signal.aborted || version !== supportScopeRef.current.version || String(conversationIdRef.current) !== String(id)) return;
      const data = unwrapAssistantPayload(payload);
      assertAssistantResponsePurpose(data, 'BUSINESS', language);
      if (String(data.id) !== String(id) || !Array.isArray(data.messages)) throw new AssistantWorkResponseError(texts.unavailable);
      setMessages(normalizeConversationMessages(data.messages, texts.welcome));
    },
  });
  const isProcessing = isProcessingState || work.submitting;

  const resetPanelWidth = () => {
    setPanelWidth(null);
    setIsExpanded(false);
  };

  const finishPanelResize = (handle?: HTMLDivElement | null) => {
    const session = panelResizeSessionRef.current;
    if (session && handle?.hasPointerCapture(session.pointerId)) {
      handle.releasePointerCapture(session.pointerId);
    }
    panelResizeSessionRef.current = null;
    setIsResizing(false);
    if (typeof document !== 'undefined') {
      document.body.classList.remove('qp-global-voice-resizing');
    }
  };

  const handlePanelResizePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || typeof window === 'undefined' || window.innerWidth <= 768) return;
    const root = rootRef.current;
    if (!root) return;

    const startWidth = clampGlobalAssistantPanelWidth(root.getBoundingClientRect().width);
    const rtl = window.getComputedStyle(root).direction === 'rtl';
    panelResizeSessionRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth,
      rtl,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    setPanelWidth(startWidth);
    setIsExpanded(false);
    setIsResizing(true);
    document.body.classList.add('qp-global-voice-resizing');
  };

  const handlePanelResizePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const session = panelResizeSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - session.startX;
    const nextWidth = session.startWidth + (session.rtl ? deltaX : -deltaX);
    setPanelWidth(clampGlobalAssistantPanelWidth(nextWidth));
  };

  const handlePanelResizePointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const session = panelResizeSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    finishPanelResize(event.currentTarget);
  };

  const handlePanelResizeKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const root = rootRef.current;
    if (!root) return;
    if (event.key === 'Home' || event.key === 'Escape') {
      event.preventDefault();
      resetPanelWidth();
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const rtl = window.getComputedStyle(root).direction === 'rtl';
    const physicalDirection = event.key === 'ArrowRight' ? 1 : -1;
    const widthDirection = rtl ? physicalDirection : -physicalDirection;
    setIsExpanded(false);
    setPanelWidth(clampGlobalAssistantPanelWidth(
      root.getBoundingClientRect().width
        + widthDirection * GLOBAL_ASSISTANT_PANEL_KEYBOARD_STEP,
    ));
  };

  const stopSpeechRecognition = () => {
    const r = recognitionRef.current;
    recognitionRef.current = null;
    try {
      r?.stop?.();
    } catch {
      /* ignore */
    }
    setIsListening(false);
  };

  const toggleSpeechRead = () => {
    setSpeechReadEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SPEECH_READ_STORAGE_KEY, String(next));
      } catch {
        /* private mode */
      }
      if (!next && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      return next;
    });
  };

  useEffect(() => { stopSpeechRecognition(); }, [supportDescriptionMode]);

  // Adım adım akış: sorun metni (draft) yoksa yalnızca "Ana menü" göster — kullanıcı önce sorununu yazar.
  // Sorun yazıldıktan sonra: Ekran görüntüsü ekle + Kaydı gönder + Ana menü.
  const buildSupportMenuActions = (draft: Record<string, unknown> | null = pendingSupportDraft): AssistantSuggestedAction[] => {
    const actions: AssistantSuggestedAction[] = [];
    if (draft) {
      actions.push({
        type: 'capture_screenshot',
        label: texts.supportAddImageLabel || getScreenCaptureLabels(language).add,
        requiresConfirmation: false,
      });
      actions.push({
        type: 'open_support_ticket',
        label: texts.supportSendLabel || 'Gönder',
        requiresConfirmation: false,
        payload: { draft },
      });
    }
    actions.push({
      type: 'support_main_menu',
      label: texts.supportMainMenuLabel || 'Ana menü',
      requiresConfirmation: false,
    });
    return actions;
  };

  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop?.();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (!engaged) {
      stopSpeechRecognition();
    }
  }, [engaged]);

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0]?.id !== '1' || prev[0]?.role !== 'ai') return prev;
      const welcome = purpose === 'QUBEPRO_HELP' ? purposeTexts.helpHint : texts.welcome;
      if (prev[0].content === welcome) return prev;
      return [{ ...prev[0], content: welcome }];
    });
  }, [purpose, purposeTexts.helpHint, texts.welcome]);

  useEffect(() => {
    if (activationNonce > 0) {
      setEngaged(true);
      setIsOpen(true);
    } else {
      setEngaged(false);
      setIsOpen(false);
    }
  }, [activationNonce]);

  useEffect(() => {
    if (panelExpandKey > 0 && engaged) {
      setIsOpen(true);
    }
  }, [panelExpandKey, engaged]);

  useEffect(() => {
    onPanelOpenChange?.(Boolean(engaged && isOpen));
  }, [engaged, isOpen, onPanelOpenChange]);

  useEffect(() => {
    onPanelExpandedChange?.(isExpanded || panelWidth !== null);
  }, [isExpanded, panelWidth, onPanelExpandedChange]);

  useEffect(() => {
    const root = rootRef.current;
    const layout = root?.closest<HTMLElement>('.layout') ?? null;
    const useLegacyDock = Boolean(root && layout && root.parentElement === layout && engaged && isOpen);
    if (!layout || !useLegacyDock) return undefined;

    layout.classList.add('qp-global-voice-legacy-docked');
    layout.classList.toggle('qp-global-voice-legacy-expanded', isExpanded && panelWidth == null);
    layout.classList.toggle('qp-global-voice-legacy-resizing', isResizing);
    if (panelWidth == null) {
      layout.style.removeProperty('--qp-global-voice-panel-width');
    } else {
      layout.style.setProperty('--qp-global-voice-panel-width', `${panelWidth}px`);
    }
    return () => {
      layout.classList.remove(
        'qp-global-voice-legacy-docked',
        'qp-global-voice-legacy-expanded',
        'qp-global-voice-legacy-resizing',
      );
      layout.style.removeProperty('--qp-global-voice-panel-width');
    };
  }, [engaged, isExpanded, isOpen, isResizing, panelWidth]);

  useEffect(() => {
    const root = rootRef.current;
    const layout = root?.closest<HTMLElement>('.layout') ?? null;
    const useLegacyDock = Boolean(root && layout && root.parentElement === layout && engaged && isOpen);
    if (!root || !useLegacyDock || typeof window === 'undefined') return undefined;

    let animationFrame = 0;
    const publishSafeArea = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const width = Math.round(root.getBoundingClientRect().width);
        const rtl = window.getComputedStyle(root).direction === 'rtl';
        setQubeProFloatingSafeArea(
          GLOBAL_ASSISTANT_FLOATING_SAFE_AREA_SOURCE,
          rtl ? { left: width, right: 0 } : { left: 0, right: width },
        );
      });
    };

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(publishSafeArea);
    resizeObserver?.observe(root);
    window.addEventListener('resize', publishSafeArea);
    publishSafeArea();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', publishSafeArea);
      resizeObserver?.disconnect();
      setQubeProFloatingSafeArea(GLOBAL_ASSISTANT_FLOATING_SAFE_AREA_SOURCE, null);
    };
  }, [engaged, isOpen]);

  useEffect(() => () => {
    panelResizeSessionRef.current = null;
    if (typeof document !== 'undefined') {
      document.body.classList.remove('qp-global-voice-resizing');
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const toggleListening = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      void showQubeProNotice({
        language: feedbackLanguage,
        variant: 'warning',
        message: texts.unsupported,
      });
      return;
    }

    if (recognitionRef.current) {
      stopSpeechRecognition();
      return;
    }

    voiceDictationPrefixRef.current = supportDescriptionMode ? supportForm.description : inputValue;
    const dictatingSupportForm = supportDescriptionMode;

    const recognition = new SR();
    const voiceScopeVersion = supportScopeRef.current.version;
    const isCurrentRecognition = () => voiceScopeVersion === supportScopeRef.current.version
      && recognitionRef.current === recognition;
    recognition.lang = voiceLocale;
    recognition.continuous = true;
    recognition.interimResults = true;
    try {
      recognition.maxAlternatives = 1;
    } catch {
      /* bazı motorlarda yok */
    }

    recognition.onstart = () => { if (isCurrentRecognition()) setIsListening(true); };

    /**
     * Tüm oturum sonuçlarını (0..length-1) birleştirir; ara + kesin metin birlikte gelir.
     * Yalnızca `results[length-1]` okumak birçok Chromium sürümünde boş / eksik transcript üretir.
     */
    recognition.onresult = (event: any) => {
      if (!isCurrentRecognition()) return;
      const list = event?.results;
      if (!list?.length) return;
      let spoken = '';
      for (let i = 0; i < list.length; i++) {
        const alt = list[i]?.[0];
        const t = typeof alt?.transcript === 'string' ? alt.transcript : '';
        spoken += t;
      }
      const prefix = (voiceDictationPrefixRef.current || '').replace(/\s+$/, '');
      const tail = spoken.trim();
      const combined = [prefix, tail].filter(Boolean).join(' ');
      if (combined) {
        if (dictatingSupportForm) {
          setSupportFormFailure(null);
          setSupportForm(current => ({ ...current, description: combined }));
        } else setInputValue(combined);
      }
    };

    recognition.onerror = (event: any) => {
      if (!isCurrentRecognition()) return;
      const err = String(event.error || '');
      if (err !== 'aborted' && err !== 'no-speech') {
        if (err === 'not-allowed') {
          void showQubeProNotice({
            language: feedbackLanguage,
            variant: 'warning',
            message: texts.micDenied,
          });
        } else if (err === 'network') {
          void showQubeProNotice({
            language: feedbackLanguage,
            variant: 'warning',
            message: texts.speechNetwork,
          });
        }
      }
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognition.onend = () => {
      if (!isCurrentRecognition()) return;
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    setIsOpen(true);
    try {
      recognition.start();
    } catch (e) {
      console.error(e);
      recognitionRef.current = null;
      setIsListening(false);
      void showQubeProNotice({
        language: feedbackLanguage,
        variant: 'warning',
        message: texts.speechStartFailed,
      });
    }
  };

  const captureScreen = async () => {
    if (captureInProgressRef.current) return;
    captureInProgressRef.current = true;
    setIsCapturingScreen(true);
    const supportTarget = supportTargetForPurpose(purposeRef.current);
    setSupportForm(current => ({ ...current, supportTarget }));
    setSupportDescriptionMode(true);
    const scopeVersion = supportScopeRef.current.version;
    const labels = getScreenCaptureLabels(language);
    let captured = false;
    try {
      const file = await captureAssistantPageScreenshot();
      captured = true;
      const attachmentId = createAssistantId('screen');
      if (scopeVersion !== supportScopeRef.current.version) return;
      setLocalScreenshot({ file, url: URL.createObjectURL(file), scope: scopeVersion });
      const attachment = await uploadAssistantAttachment(
        apiBasePath,
        file,
        supportTarget,
        attachmentId,
        companyId,
      );
      if (scopeVersion !== supportScopeRef.current.version) return;
      setAttachments((prev) => [attachment, ...prev.filter((item) => item.id !== attachment.id)]);
      setLocalScreenshot(null);
      appendAssistantMessage(labels.added, buildSupportMenuActions());
      focusSupportDescriptionInput(false);
    } catch {
      if (scopeVersion !== supportScopeRef.current.version) return;
      appendAssistantMessage(captured ? labels.uploadFailed : labels.failed);
    } finally {
      captureInProgressRef.current = false;
      setIsCapturingScreen(false);
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const supportTarget = supportTargetForPurpose(purposeRef.current);
    setSupportForm(current => ({ ...current, supportTarget }));
    setSupportDescriptionMode(true);
    const scopeVersion = supportScopeRef.current.version;
    const selectedFiles = Array.from(files);
    const isTurkish = resolveAssistantLanguageCode(language) === 'tr';

    try {
      const nextAttachments = await Promise.all(
        selectedFiles.map(async (file) => uploadAssistantAttachment(
          apiBasePath,
          file,
          supportTarget,
          createAssistantId('file'),
          companyId,
        )),
      );
      if (scopeVersion !== supportScopeRef.current.version) return;
      setAttachments((prev) => [...nextAttachments, ...prev]);
      appendAssistantMessage(attachFileAddedMessage, buildSupportMenuActions());
      focusSupportDescriptionInput(false);
    } catch {
      if (scopeVersion !== supportScopeRef.current.version) return;
      appendAssistantMessage(isTurkish ? 'Ekler DMS\'e yüklenemedi. Dosyayı tekrar seçmeyi deneyin.' : 'Attachments could not be uploaded to DMS. Try selecting the file again.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const buildCurrentAssistantContext = (): {
    providedContext: GlobalVoiceAssistantContext;
    pageContext: Record<string, unknown>;
    uiContext: Record<string, unknown>;
    attachments: ReturnType<typeof buildAssistantAttachmentPayload>;
    applicationCode: string;
  } => {
    if (purposeRef.current === 'QUBEPRO_HELP') return {
      providedContext: {}, pageContext: {}, uiContext: {},
      attachments: buildAssistantAttachmentPayload(attachments),
      applicationCode: applicationCode || getDefaultApplicationCode(),
    };
    const providedContext = contextProvider?.() || {};
    const attachmentSummary = summarizeAssistantAttachments(attachments);
    const screenSnapshot = buildScreenSnapshotContext();
    const pageContext = {
      path: window.location.pathname,
      search: sanitizeSearch(window.location.search),
      title: document.title,
      url: sanitizeCurrentUrl(),
      viewport: screenSnapshot.viewport,
      ...(providedContext.pageContext || {}),
    };
    const uiContext = {
      ...(providedContext.uiContext || {}),
      screenSnapshot,
      attachments: attachmentSummary,
    };

    return {
      providedContext,
      pageContext,
      uiContext,
      attachments: buildAssistantAttachmentPayload(attachments),
      applicationCode: applicationCode || getDefaultApplicationCode(),
    };
  };

  const readContextText = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  const buildLocalSupportDraft = (
    rawDescription: string,
    currentContext: ReturnType<typeof buildCurrentAssistantContext>,
  ): Record<string, unknown> => {
    const cleanDescription = rawDescription.trim().replace(/\s+/g, ' ');
    const normalizedDescription = normalizeCommandText(cleanDescription);
    const product = String(currentContext.applicationCode || 'CORE');
    const shortTitle = cleanDescription.length > 90
      ? `${cleanDescription.slice(0, 87).trim()}...`
      : cleanDescription;
    const priority = /(acil|kritik|coktu|calismiyor|urgent|critical|down)/.test(normalizedDescription)
      ? 'high'
      : 'medium';
    const category = /(hata|bug|problem|sorun|error|calismiyor)/.test(normalizedDescription)
        ? 'ISSUE'
        : /(oneri|öneri|geri bildirim|feedback|idea)/.test(normalizedDescription)
          ? 'SUGGESTION'
          : 'REQUEST';

    return {
      title: `${product}: ${shortTitle}`,
      description: cleanDescription,
      product,
      priority,
      category,
      supportTarget: null,
      reproductionSteps: [],
    };
  };

  const speakAssistantResponse = (content: string) => {
    if (!speechReadEnabled || !('speechSynthesis' in window)) return;
    const utterance = new SpeechSynthesisUtterance(content);
    utterance.lang = voiceLocale;
    const voices = window.speechSynthesis.getVoices?.() || [];
    const voice = voices.find((item) => item.lang?.toLowerCase() === voiceLocale.toLowerCase())
      || voices.find((item) => item.lang?.toLowerCase().startsWith(voiceLocale.slice(0, 2).toLowerCase()));
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
  };

  const appendAssistantMessage = (
    content: string,
    actions?: AssistantSuggestedAction[],
    dataView?: AssistantDataView,
    dataPresentation: AssistantDataPresentation = 'TABLE',
    verifiedAnalysis?: AssistantVerifiedAnalysis,
    sources?: AssistantDataSource[],
    routing?: Pick<Message, 'assistant' | 'routingMode' | 'expertContributions'>,
  ) => {
    const aiMsg: Message = {
      id: `${Date.now()}ai${Math.random().toString(36).slice(2, 7)}`,
      role: 'ai',
      content,
      timestamp: createAssistantMessageTimestamp(),
      actions,
      dataView,
      dataPresentation,
      verifiedAnalysis,
      sources,
      ...routing,
    };
    setMessages((prev) => [...prev, aiMsg]);
    speakAssistantResponse(content);
  };

  const resetConversation = (preserveDraft = false) => {
    // Invalidate callbacks synchronously, including events emitted by abort().
    supportScopeRef.current = {
      key: `${normalizeAssistantCompanyId(companyId) ?? ''}:${selectedAssistantRef.current?.id ?? ''}:${supportTargetForPurpose(purposeRef.current)}`,
      version: supportScopeRef.current.version + 1,
    };
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    voiceDictationPrefixRef.current = '';
    setIsListening(false);
    if (typeof window !== 'undefined') window.speechSynthesis?.cancel();
    historyRequestIdRef.current += 1;
    setHistoryLoading(false);
    setHistoryError('');
    chatRequestAbortControllerRef.current?.abort();
    setIsChatRequestActive(false);
    productFormRequestRef.current?.abort();
    productFormRequestRef.current = null;
    setIsProcessing(false);
    setPendingActionId(null);
    setDraftReview(null);
    setSupportForm({ ...SUPPORT_FORM_INITIAL, supportTarget: supportTargetForPurpose(purposeRef.current) });
    setConversationId(null);
    conversationIdRef.current = null;
    setMessages([{
      id: '1',
      role: 'ai',
      content: purposeRef.current === 'QUBEPRO_HELP' ? purposeTexts.helpHint : texts.welcome,
      timestamp: createAssistantMessageTimestamp(),
    }]);
    if (!preserveDraft) setInputValue('');
    setAttachments([]);
    setSupportDescriptionMode(false);
    setPendingSupportDraft(null);
    workModeRef.current = 'AUTO';
    setWorkMode('AUTO');
    targetAssistantRef.current = null;
    setTargetAssistant(null);
    targetRetentionRef.current = 'ONE_SHOT';
    setTargetRetention('ONE_SHOT');
    composerMentionsRef.current = [];
    setComposerMentions([]);
    setWorkModeMenuOpen(false);
    setAssistantPickerOpen(false);
  };

  const previousSupportCompanyRef = useRef(companyId);
  useEffect(() => {
    if (previousSupportCompanyRef.current === companyId) return;
    previousSupportCompanyRef.current = companyId;
    resetConversation();
    setHistoryItems([]);
    setHistoryOpen(false);
  }, [companyId]);

  const startNewConversation = () => {
    resetConversation();
    setAssistantMenuOpen(false);
    setHistoryOpen(false);
  };

  const changeConversationPurpose = (next: AssistantConversationPurpose) => {
    if (next === purposeRef.current) return;
    purposeRef.current = next;
    setPurpose(next);
    resetConversation();
    setHistoryItems([]);
    setHistoryOpen(false);
    setAssistantMenuOpen(false);
  };
  const ensureHelpConversation = () => changeConversationPurpose('QUBEPRO_HELP');

  const loadAvailableAssistants = async (query = assistantPickerQuery) => {
    const requestId = assistantPickerRequestIdRef.current + 1;
    assistantPickerRequestIdRef.current = requestId;
    setAssistantPickerLoading(true);
    setAssistantPickerError('');
    try {
      const params = new URLSearchParams({ limit: '50' });
      const normalizedQuery = query.trim();
      if (normalizedQuery) params.set('q', normalizedQuery);
      const payload = await sendAssistantRequest(
        apiBasePath,
        `/available-assistants?${params.toString()}`,
        { companyId },
      );
      const assistants = normalizeAssistantIdentities(payload);
      if (requestId !== assistantPickerRequestIdRef.current) return;
      assistantPickerLoadedScopeRef.current = String(companyId ?? '');
      setAvailableAssistants(assistants);
      if (!selectedAssistantRef.current) {
        const defaultAssistant = assistants.find((assistant) => assistant.isDefault)
          || assistants.find((assistant) => assistant.isSystem)
          || null;
        if (defaultAssistant) {
          selectedAssistantRef.current = defaultAssistant;
          setSelectedAssistant(defaultAssistant);
        }
      }
    } catch (error) {
      if (requestId !== assistantPickerRequestIdRef.current) return;
      assistantPickerLoadedScopeRef.current = null;
      console.error(error);
      setAssistantPickerError(
        resolveAssistantLanguageCode(language) === 'tr'
          ? 'Erişilebilir asistanlar alınamadı.'
          : 'Accessible assistants could not be loaded.',
      );
      setAvailableAssistants([]);
    } finally {
      if (requestId === assistantPickerRequestIdRef.current) {
        setAssistantPickerLoading(false);
      }
    }
  };

  const beginAssistantPickerQuery = (query: string) => {
    assistantPickerRequestIdRef.current += 1;
    setAvailableAssistants([]);
    setAssistantPickerQuery(query);
    setAssistantPickerQueryRevision((current) => current + 1);
    setAssistantPickerLoading(true);
    setAssistantPickerError('');
  };

  useEffect(() => {
    if (!engaged || !isOpen) return undefined;
    const companyScope = String(companyId ?? '');
    if (!assistantPickerOpen && assistantPickerLoadedScopeRef.current === companyScope) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void loadAvailableAssistants(assistantPickerQuery);
    }, assistantPickerQuery ? 180 : 0);
    return () => window.clearTimeout(timer);
  }, [
    companyId,
    engaged,
    isOpen,
    assistantPickerOpen,
    assistantPickerQuery,
    assistantPickerQueryRevision,
  ]);

  const handleComposerMenuOpenChange = (open: boolean) => {
    setComposerMenuOpen(open);
  };

  const handleAssistantSelection = (assistant: AssistantIdentity) => {
    if (purposeRef.current !== 'BUSINESS') return;
    assistantPickerRequestIdRef.current += 1;
    if (workModeRef.current === 'EXPERT_BOARD' || assistantPickerPurpose === 'BOARD') {
      const currentMentions = composerMentionsRef.current;
      const alreadySelected = currentMentions.some((mention) => String(mention.id) === String(assistant.id));
      if (!alreadySelected && new Set(currentMentions.map((mention) => String(mention.id))).size >= 5) {
        void showQubeProNotice({
          language: feedbackLanguage,
          variant: 'warning',
          message: resolveAssistantLanguageCode(language) === 'tr'
            ? 'Uzman Masası en fazla 5 yetkili uzmanla çalışır.'
            : 'Expert desk supports up to 5 authorized experts.',
        });
        return;
      }
      const next = insertAssistantComposerMention(inputValue, currentMentions, assistant);
      composerMentionsRef.current = next.mentions;
      setComposerMentions(next.mentions);
      setInputValue(next.value);
      workModeRef.current = 'EXPERT_BOARD';
      setWorkMode('EXPERT_BOARD');
      targetAssistantRef.current = null;
      setTargetAssistant(null);
      targetRetentionRef.current = 'ONE_SHOT';
      setTargetRetention('ONE_SHOT');
      setAssistantPickerOpen(false);
      setAssistantPickerQuery('');
      setAssistantPickerLoading(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }
    targetAssistantRef.current = assistant;
    setTargetAssistant(assistant);
    const retention: AssistantTargetRetention = assistantPickerPurpose === 'PINNED' ? 'PINNED' : 'ONE_SHOT';
    targetRetentionRef.current = retention;
    setTargetRetention(retention);
    const nextMode: AssistantWorkMode = retention === 'PINNED' ? 'SINGLE_EXPERT' : 'AUTO';
    workModeRef.current = nextMode;
    setWorkMode(nextMode);
    setAssistantPickerOpen(false);
    setAssistantPickerQuery('');
    setAssistantPickerLoading(false);
    composerMentionsRef.current = [];
    setComposerMentions([]);
    setInputValue((current) => current.replace(/(^|\s)@[^@\n]*$/u, '$1'));
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const clearTargetAssistant = () => {
    targetAssistantRef.current = null;
    setTargetAssistant(null);
    targetRetentionRef.current = 'ONE_SHOT';
    setTargetRetention('ONE_SHOT');
    composerMentionsRef.current = [];
    setComposerMentions([]);
    workModeRef.current = 'AUTO';
    setWorkMode('AUTO');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleWorkModeSelection = (nextMode: AssistantWorkMode) => {
    if (purposeRef.current !== 'BUSINESS') return;
    setComposerMenuOpen(false);
    if (nextMode === 'SINGLE_EXPERT') {
      setAssistantPickerPurpose('PINNED');
      setAssistantPickerQuery('');
      setAssistantPickerOpen(true);
      return;
    }
    if (nextMode === 'EXPERT_BOARD') {
      targetAssistantRef.current = null;
      setTargetAssistant(null);
      targetRetentionRef.current = 'ONE_SHOT';
      setTargetRetention('ONE_SHOT');
      workModeRef.current = nextMode;
      setWorkMode(nextMode);
      setAssistantPickerPurpose('BOARD');
      setAssistantPickerQuery('');
      setAssistantPickerOpen(true);
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }
    targetAssistantRef.current = null;
    setTargetAssistant(null);
    targetRetentionRef.current = 'ONE_SHOT';
    setTargetRetention('ONE_SHOT');
    composerMentionsRef.current = [];
    setComposerMentions([]);
    workModeRef.current = nextMode;
    setWorkMode(nextMode);
    assistantPickerRequestIdRef.current += 1;
    setAssistantPickerOpen(false);
    setAssistantPickerQuery('');
    setAssistantPickerLoading(false);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleComposerValueChange = (value: string) => {
    setInputValue(value);
    const mention = purposeRef.current === 'BUSINESS' ? /(?:^|\s)@([^@\n]*)$/u.exec(value) : null;
    if (mention) {
      setAssistantPickerPurpose(workModeRef.current === 'EXPERT_BOARD' ? 'BOARD' : 'MENTION');
      beginAssistantPickerQuery((mention[1] || '').trim());
      setAssistantPickerOpen(true);
    } else {
      assistantPickerRequestIdRef.current += 1;
      setAssistantPickerOpen(false);
      setAssistantPickerQuery('');
      setAssistantPickerLoading(false);
    }
  };

  const recordConversationEvents = async (
    events: GlobalAssistantConversationEventRecord[],
  ) => {
    const validEvents = events.filter((event) => event.content.trim());
    if (validEvents.length === 0) return;
    const scopeVersion = supportScopeRef.current.version;
    const requestPurpose = purposeRef.current;
    try {
      const currentContext = buildCurrentAssistantContext();
      const mode = inferGlobalAssistantConversationMode(validEvents);
      const payload = await sendAssistantPostRequest(apiBasePath, assistantPurposePath('/conversations/events', requestPurpose), {
        purpose: requestPurpose,
        conversationId: conversationIdRef.current,
        assistantId: selectedAssistantRef.current?.id,
        applicationCode: currentContext.applicationCode,
        ...(requestPurpose === 'BUSINESS' ? {
          pageContext: currentContext.pageContext,
          userContext: currentContext.providedContext.userContext,
          companyContext: currentContext.providedContext.companyContext,
          uiContext: currentContext.uiContext,
          attachments: currentContext.attachments,
        } : {}),
        messages: validEvents.map((event) => ({
          role: event.role,
          content: event.content,
          timestamp: normalizeAssistantMessageTimestamp(event.timestamp)
            ?? createAssistantMessageTimestamp(),
          ...(requestPurpose === 'BUSINESS' ? { metadata: event.metadata } : {}),
        })),
      }, companyId);
      if (scopeVersion !== supportScopeRef.current.version) return;
      const data = unwrapAssistantPayload(payload);
      assertAssistantResponsePurpose(data, requestPurpose, language);
      const nextConversationId = data?.conversationId ?? conversationIdRef.current;
      if (data?.conversationId != null) {
        conversationIdRef.current = data.conversationId;
        setConversationId(data.conversationId);
      }
      notifyGlobalAssistantConversationUpdated({
        conversationId: nextConversationId,
        assistantId: selectedAssistantRef.current?.id,
        applicationCode: currentContext.applicationCode,
        mode,
      });
    } catch (error) {
      if (scopeVersion !== supportScopeRef.current.version) return;
      console.error('Global assistant history save failed', error);
    }
  };

  const loadConversations = async (
    archived = showArchivedConversations,
    productScope: AssistantHistoryProductScope = historyProductScope,
  ) => {
    const requestId = ++historyRequestIdRef.current;
    const requestPurpose = purposeRef.current;
    const scopeVersion = supportScopeRef.current.version;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const currentContext = buildCurrentAssistantContext();
      const params = new URLSearchParams({
        size: '20',
        archived: archived ? 'archived' : 'active',
      });
      if (productScope === 'current' && currentContext.applicationCode) {
        params.set('applicationCode', currentContext.applicationCode);
      }
      if (selectedAssistantRef.current?.id != null) {
        params.set('assistantId', String(selectedAssistantRef.current.id));
      }
      const payload = await sendAssistantRequest(
        apiBasePath,
        assistantPurposePath(`/conversations?${params.toString()}`, requestPurpose),
        { companyId },
      );
      if (requestId !== historyRequestIdRef.current || scopeVersion !== supportScopeRef.current.version) return;
      const data = unwrapAssistantPayload(payload);
      setHistoryItems(normalizeConversationItems(data.data).filter(item => item.purpose === requestPurpose));
    } catch (error) {
      if (requestId !== historyRequestIdRef.current || scopeVersion !== supportScopeRef.current.version) return;
      console.error(error);
      setHistoryError(texts.historyLoadFailed || 'Konuşma geçmişi alınamadı.');
    } finally {
      if (requestId === historyRequestIdRef.current && scopeVersion === supportScopeRef.current.version) setHistoryLoading(false);
    }
  };

  const toggleAssistantMenu = async () => {
    const next = !assistantMenuOpen;
    setAssistantMenuOpen(next);
    if (!next) {
      setHistoryOpen(false);
      return;
    }
    await loadConversations(showArchivedConversations, historyProductScope);
  };

  const openFullHistoryView = async () => {
    setAssistantMenuOpen(true);
    setHistoryOpen(true);
    await loadConversations(showArchivedConversations, historyProductScope);
  };

  const toggleArchivedFilter = async () => {
    const next = !showArchivedConversations;
    setShowArchivedConversations(next);
    await loadConversations(next, historyProductScope);
  };

  const closeFullHistoryView = async () => {
    setHistoryOpen(false);
    if (showArchivedConversations) {
      setShowArchivedConversations(false);
      await loadConversations(false, historyProductScope);
    }
  };

  const changeHistoryProductScope = async (next: AssistantHistoryProductScope) => {
    setHistoryProductScope(next);
    await loadConversations(showArchivedConversations, next);
  };

  const openAiWorkspace = () => {
    setAssistantMenuOpen(false);
    const selectedId = selectedAssistantRef.current?.id;
    const target = new URL(
      buildAiAppUrl(apiBasePath, selectedId != null ? `assistant/${encodeURIComponent(String(selectedId))}` : 'assistant'),
      window.location.origin,
    );
    target.searchParams.set('purpose', purposeRef.current);
    if (conversationId != null && String(conversationId).trim()) {
      target.searchParams.set('conversationId', String(conversationId));
      target.searchParams.set('source', 'global-assistant');
    }
    window.location.assign(target.toString());
  };

  const openAiSettings = () => {
    setAssistantMenuOpen(false);
    window.location.assign(buildAiAppUrl(apiBasePath, 'assistants'));
  };

  const openFeedbackSupportForm = () => {
    ensureHelpConversation();
    supportScopeRef.current.version += 1;
    const currentContext = buildCurrentAssistantContext();
    const product = currentContext.applicationCode || 'QubePro';

    setSupportForm({
      ...SUPPORT_FORM_INITIAL,
      supportTarget: 'QUBEPRO_SAAS',
      title: `${product}: Geri bildirim`,
      category: 'SUGGESTION',
      priority: 'medium',
    });
    setPendingSupportDraft(null);
    setSupportDescriptionMode(true);
    setAssistantMenuOpen(false);
    setHistoryOpen(false);
    setAttachments([]);
    setInputValue('');
    setIsOpen(true);
  };

  const openConversation = async (id: number | string) => {
    resetConversation();
    const requestId = ++historyRequestIdRef.current;
    const requestPurpose = purposeRef.current;
    const scopeVersion = supportScopeRef.current.version;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const payload = await sendAssistantRequest(
        apiBasePath,
        assistantPurposePath(`/conversations/${encodeURIComponent(String(id))}`, requestPurpose),
        { companyId },
      );
      if (requestId !== historyRequestIdRef.current || scopeVersion !== supportScopeRef.current.version) return;
      const data = unwrapAssistantPayload(payload);
      assertAssistantResponsePurpose(data, requestPurpose, language);
      conversationIdRef.current = data.id ?? id;
      setConversationId(data.id ?? id);
      const conversationAssistant = normalizeAssistantIdentity(data.assistant);
      if (conversationAssistant) {
        selectedAssistantRef.current = conversationAssistant;
        setSelectedAssistant(conversationAssistant);
      }
      workModeRef.current = 'AUTO';
      setWorkMode('AUTO');
      targetAssistantRef.current = null;
      setTargetAssistant(null);
      targetRetentionRef.current = 'ONE_SHOT';
      setTargetRetention('ONE_SHOT');
      setMessages(normalizeConversationMessages(data.messages, texts.welcome));
      setSupportDescriptionMode(false);
      setPendingSupportDraft(null);
      setAttachments([]);
      setHistoryOpen(false);
      setAssistantMenuOpen(false);
    } catch (error) {
      if (requestId !== historyRequestIdRef.current || scopeVersion !== supportScopeRef.current.version) return;
      console.error(error);
      setHistoryError(texts.historyLoadFailed || 'Konuşma geçmişi alınamadı.');
    } finally {
      if (requestId === historyRequestIdRef.current && scopeVersion === supportScopeRef.current.version) setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!engaged || !isOpen || conversationId || restoredConversationLoadedRef.current) return;
    const restoredId = restoredConversationIdRef.current;
    if (!restoredId) return;
    restoredConversationLoadedRef.current = true;
    void openConversation(restoredId).catch(() => {
      restoredConversationIdRef.current = '';
      writeAiStringPref(LS_AI_ACTIVE_CONVERSATION_ID, null);
    });
  }, [engaged, isOpen, conversationId]);

  const archiveConversation = async (id: number | string, archived: boolean) => {
    const scopeVersion = supportScopeRef.current.version;
    const requestPurpose = purposeRef.current;    setHistoryLoading(true);
    setHistoryError('');
    try {
      const assistantScope = selectedAssistantRef.current?.id != null
        ? `?assistantId=${encodeURIComponent(String(selectedAssistantRef.current.id))}`
        : '';
      await sendAssistantRequest(apiBasePath, assistantPurposePath(`/conversations/${encodeURIComponent(String(id))}/archive${assistantScope}`, requestPurpose), {
        method: 'PATCH',
        body: { archived },
        companyId,
      });
      if (scopeVersion !== supportScopeRef.current.version) return;
      if (String(conversationId || '') === String(id) && archived) resetConversation();
      await loadConversations(showArchivedConversations, historyProductScope);
    } catch (error) {
      if (scopeVersion !== supportScopeRef.current.version) return;
      console.error(error);
      setHistoryError(texts.historyLoadFailed || 'Konuşma geçmişi alınamadı.');
    } finally {
      if (scopeVersion === supportScopeRef.current.version) setHistoryLoading(false);
    }
  };

  const deleteConversation = async (id: number | string, title?: string) => {
    const scopeVersion = supportScopeRef.current.version;
    const requestPurpose = purposeRef.current;    const normalizedTitle = String(title || '').trim();
    const deleteMessage = isTurkishAssistant
      ? normalizedTitle
        ? `"${normalizedTitle}" konuşmasını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
        : 'Bu konuşmayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.'
      : normalizedTitle
        ? `Delete "${normalizedTitle}"? This action cannot be undone.`
        : 'Delete this conversation? This action cannot be undone.';
    const confirmed = await showQubeProConfirmDialog({
      language: feedbackLanguage,
      variant: 'danger',
      title: texts.deleteConversationTitle || 'Sil',
      message: deleteMessage,
    });
    if (!confirmed || scopeVersion !== supportScopeRef.current.version) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const assistantScope = selectedAssistantRef.current?.id != null
        ? `?assistantId=${encodeURIComponent(String(selectedAssistantRef.current.id))}`
        : '';
      await sendAssistantRequest(apiBasePath, assistantPurposePath(`/conversations/${encodeURIComponent(String(id))}${assistantScope}`, requestPurpose), {
        method: 'DELETE',
        companyId,
      });
      if (scopeVersion !== supportScopeRef.current.version) return;
      if (String(conversationId || '') === String(id)) resetConversation();
      await loadConversations(showArchivedConversations, historyProductScope);
    } catch (error) {
      if (scopeVersion !== supportScopeRef.current.version) return;
      console.error(error);
      setHistoryError(texts.historyLoadFailed || 'Konuşma geçmişi alınamadı.');
    } finally {
      if (scopeVersion === supportScopeRef.current.version) setHistoryLoading(false);
    }
  };

  const supportWritePromptText = () =>
    texts.supportWritePrompt ||
    'Destek kaydı oluşturuyoruz. Lütfen sorununuzu alttaki alana yazın: ne oldu, ne bekliyordunuz, varsa hata mesajı.';

  const openSupportMenu = (draft: Record<string, unknown> | null = pendingSupportDraft) => {
    setSupportForm(current => ({ ...current, supportTarget: supportTargetForPurpose(purposeRef.current) }));
    if (draft) {
      setSupportForm((current) => ({
        ...current,
        title: readContextText(draft.title),
        description: readContextText(draft.description),
        category: readContextText(draft.category) || SUPPORT_FORM_INITIAL.category,
        priority: readContextText(draft.priority) || SUPPORT_FORM_INITIAL.priority,
        steps: Array.isArray(draft.reproductionSteps) ? draft.reproductionSteps.filter((step): step is string => typeof step === 'string').join('\n') : '',
      }));
    }
    setSupportDescriptionMode(true);
    setIsOpen(true);
    const message = draft
      ? (texts.supportDraftReadyMessage ||
          'Açıklamanızı aldım. Kaydı gönderebilir, resim ekleyebilir veya açıklamayı güncelleyebilirsiniz.')
      : supportWritePromptText();
    appendAssistantMessage(message, buildSupportMenuActions(draft));
  };

  const returnToMainMenu = () => {
    supportScopeRef.current.version += 1;
    setAttachments([]);
    setSupportDescriptionMode(false);
    setPendingSupportDraft(null);
    setSupportForm({ ...SUPPORT_FORM_INITIAL, supportTarget: supportTargetForPurpose(purposeRef.current) });
    setIsOpen(true);
    appendAssistantMessage(texts.supportMainMenuMessage || 'Ana menüye döndüm.');
  };

  const focusSupportDescriptionInput = (announce = false) => {
    setSupportForm(current => ({ ...current, supportTarget: supportTargetForPurpose(purposeRef.current) }));
    setSupportDescriptionMode(true);
    setIsOpen(true);
    supportDescriptionRef.current?.focus();
    if (announce) {
      const message = texts.supportDescriptionFocusMessage ||
        'Sorununuzu destek formundaki açıklama alanına yazabilirsiniz. Kaydı göndermeden önce içeriği inceleyebilirsiniz.';
      appendAssistantMessage(message);
      void recordConversationEvents([{
        role: 'assistant',
        content: message,
        metadata: {
          handledBy: 'local_support_flow',
          action: 'focus_support_description',
        },
      }]);
    }
  };

  const getRenderableMessageActions = (actions?: AssistantSuggestedAction[]) =>
    (actions || []).filter((action) => !SUPPORT_CONTEXT_ACTION_TYPES.has(action.type)
      && (purpose === 'BUSINESS' || !['create_report_draft', 'create_automation_draft', 'prepare_product_form'].includes(action.type)));

  const handleAssistantAction = async (action: AssistantSuggestedAction, proposalReceiptId?: string) => {
    if (isProcessing || pendingActionId) return;

    if (action.type === 'open_help_doc') {
      const currentContext = buildCurrentAssistantContext();
      const urlFromAction = isObjectRecord(action.payload) && typeof action.payload.url === 'string'
        ? action.payload.url.trim()
        : '';
      const docsUrl = urlFromAction || buildQubeProDocsUrl(
        currentContext.applicationCode,
        language,
        typeof currentContext.pageContext.path === 'string' ? currentContext.pageContext.path : undefined,
      );
      window.open(docsUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (action.type === 'capture_screenshot') {
      await captureScreen();
      return;
    }

    if (action.type === 'focus_support_description') {
      focusSupportDescriptionInput(true);
      return;
    }

    if (action.type === 'support_main_menu') {
      returnToMainMenu();
      return;
    }

    if (action.type === 'open_support_ticket') {
      const draft = getAssistantActionDraft(action);
      if (draft) openSupportMenu(draft);
      else appendAssistantMessage(texts.actionNeedsDraft, buildSupportMenuActions(null));
      return;
    }
    if (purposeRef.current !== 'BUSINESS') return;
    if (action.type === 'prepare_product_form') {
      if (productFormRequestRef.current) return;
      const intent = prepareAssistantProductForm(action, proposalReceiptId);
      const conversation = conversationIdRef.current;
      const scope = supportScopeRef.current.version;
      const copy = assistantProductFormCopy(language);
      const authority = formAuthorityRef.current;
      const stillCurrent = () => scope === supportScopeRef.current.version
        && conversation === conversationIdRef.current && purposeRef.current === 'BUSINESS'
        && authority === formAuthorityRef.current;
      if (!intent || !conversation) { appendAssistantMessage(copy.failed); return; }
      const controller = new AbortController();
      productFormRequestRef.current = controller;
      const timer = setTimeout(() => controller.abort(), 20_000);
      setPendingActionId('product-form'); setIsProcessing(true);
      try {
        const payload = await sendAssistantPostRequest(apiBasePath, assistantPurposePath('/actions/product-form', 'BUSINESS'), {
          ...intent, conversationId: conversation, assistantId: selectedAssistantRef.current?.id,
          purpose: 'BUSINESS', confirmed: true,
        }, companyId, controller.signal);
        if (controller.signal.aborted || !stillCurrent()) return;
        const url = getAssistantProductFormUrl(unwrapAssistantPayload(payload), String(companyId ?? ''), intent.capability);
        if (!url) throw new Error(copy.failed);
        window.location.assign(url);
      } catch {
        if (stillCurrent()) appendAssistantMessage(copy.failed);
      } finally {
        clearTimeout(timer);
        if (productFormRequestRef.current === controller) {
          productFormRequestRef.current = null;
          if (scope === supportScopeRef.current.version) { setIsProcessing(false); setPendingActionId(null); }
        }
      }
      return;
    }
    const draft = prepareAssistantDraftAction(action);
    if (!draft) { appendAssistantMessage(assistantDraftActionCopy(language).needsDraft); return; }
    setDraftReview({ action: draft, scope: supportScopeRef.current.version, conversationId: conversationIdRef.current });
  };

  const confirmDraftAction = async (action: AssistantDraftAction) => {
    const review = draftReview;
    if (!review || review.action !== action || purposeRef.current !== 'BUSINESS' || isProcessing || pendingActionId
      || review.scope !== supportScopeRef.current.version || review.conversationId !== conversationIdRef.current) return;
    const copy = assistantDraftActionCopy(language);
    setPendingActionId(action.type);
    setIsProcessing(true);
    setDraftReview({ ...review, error: undefined });
    try {
      const payload = await sendAssistantPostRequest(apiBasePath, assistantPurposePath(action.endpoint, 'BUSINESS'), {
        draft: action.draft,
        purpose: 'BUSINESS',
        applicationCode: applicationCode || getDefaultApplicationCode(),
        assistantId: selectedAssistantRef.current?.id,
        conversationId: review.conversationId,
        confirmed: true,
      }, companyId);
      if (review.scope !== supportScopeRef.current.version || review.conversationId !== conversationIdRef.current) return;
      const result = readAssistantDraftActionResult(action, unwrapAssistantPayload(payload), language);
      appendAssistantMessage(result.message);
      setDraftReview(null);
    } catch (error) {
      if (review.scope !== supportScopeRef.current.version || review.conversationId !== conversationIdRef.current) return;
      setDraftReview({ ...review, error: error instanceof Error ? error.message : copy.failed });
    } finally {
      if (review.scope === supportScopeRef.current.version) { setIsProcessing(false); setPendingActionId(null); }
    }
  };

  const updateSupportFormField = (field: keyof SupportFormState, value: string) => {
    if (field === 'supportTarget') return; // The selected conversation purpose owns the target.
    setSupportFormFailure(null);
    setSupportForm((current) => ({ ...current, [field]: value }));
  };

  /**
   * Formdan DOĞRUDAN destek kaydı oluştur — AI taslak adımını atlar.
   * Kullanıcı formu kendi doldurduğunda gereksiz tur olmasın diye onay POST'unu
   * (handleAssistantAction ile aynı uç) formdaki değerlerden üretilen draft ile çağırır.
   */
  const submitSupportFormDirect = async () => {
    const submittedPurpose = purposeRef.current;
    if (isProcessing || pendingActionId) return;
    if (isCapturingScreen || localScreenshot?.scope === supportScopeRef.current.version) return;
    if (Object.keys(validateSupportForm(supportForm)).length > 0) return;

    if (supportForm.supportTarget !== supportTargetForPurpose(submittedPurpose)) {
      appendAssistantMessage(purposeTexts.unavailable);
      return;
    }
    const currentContext = buildCurrentAssistantContext();
    const formIssueText = buildSupportDisplayMessage(supportForm, '');
    const draft = buildSupportFormDraft(supportForm, currentContext.applicationCode);
    const scopeVersion = supportScopeRef.current.version;
    const submittedConversationId = conversationIdRef.current;
    const submittedTarget = supportForm.supportTarget;

    setIsProcessing(true);
    setSupportFormFailure(null);
    try {
      const payload = await sendAssistantPostRequest(apiBasePath, assistantPurposePath('/actions/support-ticket', submittedPurpose), {
        draft,
        supportTarget: supportForm.supportTarget,
        applicationCode: currentContext.applicationCode,
        conversationId: submittedConversationId,
        assistantId: selectedAssistantRef.current?.id,
        attachments: currentContext.attachments,
        confirmed: true,
      }, companyId);
      if (scopeVersion !== supportScopeRef.current.version) return;
      const data = unwrapAssistantPayload(payload);
      const receipt = readAssistantSupportReceipt(data, submittedTarget, submittedConversationId, language);
      if (!receipt) throw new Error(texts.actionFailed);
      conversationIdRef.current = receipt.conversationId;
      setConversationId(receipt.conversationId);
      setMessages(prev => [...prev, {
        id: `${receipt.conversationId}-${Date.now()}-support`, role: 'user',
        content: formIssueText, timestamp: receipt.timestamp,
      }]);
      appendAssistantMessage(receipt.message);
      notifyGlobalAssistantConversationUpdated({
        conversationId: receipt.conversationId,
        assistantId: selectedAssistantRef.current?.id,
        applicationCode: currentContext.applicationCode,
        mode: 'support',
      });
      setSupportForm({ ...SUPPORT_FORM_INITIAL, supportTarget: supportTargetForPurpose(purposeRef.current) });
      setAttachments([]);
      setSupportDescriptionMode(false);
      setPendingSupportDraft(null);
    } catch (error) {
      if (scopeVersion !== supportScopeRef.current.version) return;
      setSupportFormFailure(isObjectRecord(error) && typeof error.supportFailure === 'string'
        ? error.supportFailure as SupportSubmissionFailure : 'uncertain');
    } finally {
      if (scopeVersion === supportScopeRef.current.version) setIsProcessing(false);
    }
  };

  const stopActiveAssistantResponse = () => {
    const controller = chatRequestAbortControllerRef.current;
    if (!controller || controller.signal.aborted) return;
    controller.abort();
    chatRequestAbortControllerRef.current = null;
    setIsChatRequestActive(false);
    productFormRequestRef.current?.abort();
    productFormRequestRef.current = null;
    setIsProcessing(false);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const submitComposer = () => {
    if (supportDescriptionMode) void submitSupportFormDirect();
    else void handleSendMessage(inputValue);
  };

  const handleSendMessage = async (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || isProcessing || work.busy() || (purposeRef.current === 'BUSINESS' && work.uncertain)) return;
    const requestPurpose = purposeRef.current;

    const requestWorkMode = workModeRef.current;
    const requestTargetAssistant = targetAssistantRef.current;
    const requestTargetRetention = targetRetentionRef.current;
    const requestExpertPrompts = requestWorkMode === 'EXPERT_BOARD'
      ? buildAssistantExpertPrompts(text, composerMentionsRef.current)
      : [];
    const supportFlowActive = supportDescriptionMode
      || attachments.length > 0
      || isSupportOpenCommand(trimmedText, texts.quickSupport);
    if (requestWorkMode === 'SINGLE_EXPERT' && !requestTargetAssistant) {
      setAssistantPickerPurpose('PINNED');
      setAssistantPickerOpen(true);
      return;
    }
    if (
      !supportFlowActive
      && requestWorkMode === 'EXPERT_BOARD'
      && (
        requestExpertPrompts.length < 2
        || requestExpertPrompts.length > 5
        || requestExpertPrompts.some((prompt) => !prompt.question.trim())
      )
    ) {
      void showQubeProNotice({
        language: feedbackLanguage,
        variant: 'warning',
        message: resolveAssistantLanguageCode(language) === 'tr'
          ? 'Uzman Masası için 2-5 yetkili uzman seçin ve her uzmanın ardından sorusunu yazın.'
          : 'Choose 2-5 authorized experts and write a question after each expert.',
      });
      return;
    }

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: trimmedText,
      timestamp: createAssistantMessageTimestamp(),
      routingMode: requestWorkMode,
      targetAssistant: requestTargetAssistant,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    composerMentionsRef.current = [];
    setComposerMentions([]);
    setIsOpen(true);

    if (supportFlowActive) {
      if (isSupportMainMenuCommand(trimmedText)) {
        const assistantContent = texts.supportMainMenuMessage || 'Ana menüye döndüm.';
        returnToMainMenu();
        await recordConversationEvents([
          { role: 'user', content: trimmedText, timestamp: userMsg.timestamp, metadata: { handledBy: 'local_support_flow' } },
          { role: 'assistant', content: assistantContent, timestamp: createAssistantMessageTimestamp(), metadata: { handledBy: 'local_support_flow' } },
        ]);
        return;
      }

      if (isSupportSendCommand(trimmedText)) {
        if (pendingSupportDraft) {
          await recordConversationEvents([
            { role: 'user', content: trimmedText, timestamp: userMsg.timestamp, metadata: { handledBy: 'local_support_flow', action: 'send_support_ticket' } },
          ]);
          await handleAssistantAction(
            {
              type: 'open_support_ticket',
              label: texts.supportSendLabel || 'Gönder',
              requiresConfirmation: false,
              payload: { draft: pendingSupportDraft },
            },
          );
        } else {
          const assistantContent = texts.actionNeedsDraft;
          appendAssistantMessage(assistantContent, buildSupportMenuActions(null));
          focusSupportDescriptionInput(false);
          await recordConversationEvents([
            { role: 'user', content: trimmedText, timestamp: userMsg.timestamp, metadata: { handledBy: 'local_support_flow' } },
            { role: 'assistant', content: assistantContent, timestamp: createAssistantMessageTimestamp(), metadata: { handledBy: 'local_support_flow' } },
          ]);
        }
        return;
      }

      const issueText = stripSupportCommand(trimmedText, texts.quickSupport);
      if (hasMeaningfulSupportIssue(issueText)) {
        const draft = buildLocalSupportDraft(issueText, buildCurrentAssistantContext());
        const assistantContent = texts.supportDraftReadyMessage ||
          'Açıklamanızı aldım. Kaydı gönderebilir, resim ekleyebilir veya açıklamayı güncelleyebilirsiniz.';
        setPendingSupportDraft(draft);
        openSupportMenu(draft);
        await recordConversationEvents([
          { role: 'user', content: trimmedText, timestamp: userMsg.timestamp, metadata: { handledBy: 'local_support_flow' } },
          { role: 'assistant', content: assistantContent, timestamp: createAssistantMessageTimestamp(), metadata: { handledBy: 'local_support_flow', supportTicketDraft: draft } },
        ]);
        return;
      }

      const assistantContent = pendingSupportDraft
        ? (texts.supportDraftReadyMessage ||
            'Açıklamanızı aldım. Kaydı gönderebilir, resim ekleyebilir veya açıklamayı güncelleyebilirsiniz.')
        : supportWritePromptText();
      openSupportMenu(pendingSupportDraft);
      focusSupportDescriptionInput(false);
      await recordConversationEvents([
        { role: 'user', content: trimmedText, timestamp: userMsg.timestamp, metadata: { handledBy: 'local_support_flow' } },
        { role: 'assistant', content: assistantContent, timestamp: createAssistantMessageTimestamp(), metadata: { handledBy: 'local_support_flow' } },
      ]);
      return;
    }

    if (requestPurpose === 'BUSINESS') {
      await work.submit({
        purpose: 'BUSINESS', message: trimmedText, conversationId: conversationIdRef.current,
        assistantId: selectedAssistantRef.current?.id, routingMode: requestWorkMode,
        targetAssistantId: requestTargetAssistant?.id,
        ...(requestWorkMode === 'EXPERT_BOARD' ? { targetAssistants: requestExpertPrompts.map(prompt => ({
          assistantId: prompt.assistantId, question: prompt.question,
        })) } : {}),
        language: resolveAssistantLanguage(language),
      });
      return;
    }
    chatRequestAbortControllerRef.current?.abort();
    const requestController = new AbortController();
    const chatScopeVersion = supportScopeRef.current.version;
    chatRequestAbortControllerRef.current = requestController;
    setIsChatRequestActive(true);
    setIsProcessing(true);
    try {
      const currentContext = buildCurrentAssistantContext();

      const payload = await sendAssistantChatRequest(apiBasePath, {
        purpose: requestPurpose,
        message: trimmedText,
        assistantId: selectedAssistantRef.current?.id,
        routingMode: requestWorkMode,
        targetAssistantId: requestTargetAssistant?.id,
        ...(requestWorkMode === 'EXPERT_BOARD' ? {
          targetAssistants: requestExpertPrompts.map((prompt) => ({
            assistantId: prompt.assistantId,
            question: prompt.question,
          })),
        } : {}),
        applicationCode: currentContext.applicationCode,
        conversationId: conversationIdRef.current,
        pageContext: currentContext.pageContext,
        userContext: currentContext.providedContext.userContext,
        companyContext: currentContext.providedContext.companyContext,
        uiContext: currentContext.uiContext,
        attachments: currentContext.attachments,
        language: resolveAssistantLanguage(language),
      }, companyId, requestController.signal);
      if (requestController.signal.aborted || chatScopeVersion !== supportScopeRef.current.version) return;
      const data = unwrapAssistantPayload(payload);
      assertAssistantResponsePurpose(data, requestPurpose, language);
      const actions = normalizeSuggestedActions(data?.suggestedActions);
      const hasSupportAction = actions.some((action) =>
        ['open_support_ticket', 'capture_screenshot', 'focus_support_description'].includes(action.type),
      );
      const nextConversationId = data?.conversationId ?? conversationId;
      if (data?.conversationId != null) {
        conversationIdRef.current = data.conversationId;
        setConversationId(data.conversationId);
      }
      notifyGlobalAssistantConversationUpdated({
        conversationId: nextConversationId,
        assistantId: selectedAssistantRef.current?.id,
        applicationCode: currentContext.applicationCode,
        mode: data?.intent === 'support_request' || hasSupportAction || attachments.length > 0 ? 'support' : 'chat',
      });
      setSupportDescriptionMode(data?.intent === 'support_request' || hasSupportAction || attachments.length > 0);
      const responseDraft = isObjectRecord(data?.supportTicketDraft) ? data.supportTicketDraft
        : actions.find((action) => action.type === 'open_support_ticket')?.payload?.draft;
      if (isObjectRecord(responseDraft)) {
        setPendingSupportDraft(responseDraft);
        openSupportMenu(responseDraft);
      }
      const aiResponseBase = data?.answer || data?.response || data?.message?.content || texts.fallbackResponse;
      const aiResponse = readAssistantDisplayText(aiResponseBase, texts.fallbackResponse);
      const responseAssistant = normalizeAssistantIdentity(data?.assistant);
      const responseRoutingMode = normalizeAssistantWorkMode(data?.routingMode) || requestWorkMode;
      const expertContributions = normalizeAssistantExpertContributions(data?.expertContributions);
      appendAssistantMessage(
        aiResponse,
        actions,
        normalizeAssistantDataView(data?.dataView),
        normalizeAssistantDataPresentation(data?.dataPresentation),
        normalizeAssistantVerifiedAnalysis(data?.verifiedAnalysis),
        normalizeAssistantDataSources(data?.sources),
        {
          assistant: responseAssistant,
          routingMode: responseRoutingMode,
          expertContributions,
        },
      );
      if (requestTargetAssistant && requestTargetRetention === 'ONE_SHOT') {
        targetAssistantRef.current = null;
        setTargetAssistant(null);
        targetRetentionRef.current = 'ONE_SHOT';
        setTargetRetention('ONE_SHOT');
        workModeRef.current = 'AUTO';
        setWorkMode('AUTO');
      }
    } catch (error) {
      if (requestController.signal.aborted || chatScopeVersion !== supportScopeRef.current.version || (error instanceof Error && error.name === 'AbortError')) return;
      console.error(error);
      const message = error instanceof Error && error.message.trim()
        ? error.message.trim()
        : texts.unavailable;
      appendAssistantMessage(message);
    } finally {
      if (chatRequestAbortControllerRef.current === requestController) {
        chatRequestAbortControllerRef.current = null;
        setIsChatRequestActive(false);
        setIsProcessing(false);
      }
    }
  };

  const isTurkishAssistant = resolveSharedAssistantLanguageCode(language) === 'tr';
  const composerTexts = getAssistantComposerTexts(language);
  const composerActionDescriptors = getAssistantComposerActionDescriptors(language);
  const composerMenuLabel = texts.composerMenuLabel || composerTexts.menuLabel;
  const attachFileAddedMessage = texts.attachFileAddedMessage || (isTurkishAssistant ? 'Dosya eklendi. Destek kaydıyla birlikte iletilecek.' : 'File attached.');
  const supportActionDescription = (action: AssistantSuggestedAction) => {
    if (action.type === 'open_support_ticket') return texts.supportSendLabel || (isTurkishAssistant ? 'Kaydı gönder' : 'Send');
    if (action.type === 'support_main_menu') return texts.supportMainMenuLabel || (isTurkishAssistant ? 'Ana menüye git' : 'Main menu');
    if (action.type === 'focus_support_description') return texts.supportAddDescriptionLabel || (isTurkishAssistant ? 'Açıklama ekle' : 'Add description');
    if (action.type === 'capture_screenshot') return composerTexts.screenDescription;
    return texts.suggestedAction;
  };
  const buildComposerMenuItem = (descriptor: AssistantComposerActionDescriptor): AssistantComposerMenuItem => {
    switch (descriptor.id) {
      case 'capture-screen':
        return {
          id: descriptor.id,
          title: descriptor.title,
          description: descriptor.description,
          iconName: descriptor.iconName,
          disabled: isProcessing,
          onSelect: () => captureScreen(),
        };
      case 'attach-file':
        return {
          id: descriptor.id,
          title: descriptor.title,
          description: descriptor.description,
          iconName: descriptor.iconName,
          disabled: isProcessing,
          onSelect: () => fileInputRef.current?.click(),
        };
      case 'support-ticket':
        return {
          id: descriptor.id,
          title: descriptor.title,
          description: descriptor.description,
          iconName: descriptor.iconName,
          disabled: isProcessing,
          onSelect: () => focusSupportDescriptionInput(true),
        };
      case 'explain-screen':
        return {
          id: descriptor.id,
          title: descriptor.title,
          description: descriptor.description,
          iconName: descriptor.iconName,
          disabled: isProcessing,
          onSelect: () => { ensureHelpConversation(); void handleSendMessage(texts.quickHelp || descriptor.title); },
        };
      case 'report-draft':
        return {
          id: descriptor.id,
          title: descriptor.title,
          description: descriptor.description,
          iconName: descriptor.iconName,
          disabled: isProcessing,
          onSelect: () => handleSendMessage(texts.quickReport || descriptor.title),
        };
      case 'automation-draft':
      default:
        return {
          id: descriptor.id,
          title: descriptor.title,
          description: descriptor.description,
          iconName: descriptor.iconName,
          disabled: isProcessing,
          onSelect: () => handleSendMessage(texts.quickAutomation || descriptor.title),
        };
    }
  };
  const contextualComposerMenuItems: AssistantComposerMenuItem[] = supportFlowActive
    ? [
        {
          id: 'attach-file',
          title: composerTexts.fileTitle,
          description: composerTexts.fileDescription,
          icon: <Paperclip size={16} aria-hidden />,
          disabled: isProcessing,
          onSelect: () => fileInputRef.current?.click(),
        },
        ...buildSupportMenuActions().map((action, index) => ({
          id: `support-context-${action.type}-${index}`,
          title: action.label,
          description: supportActionDescription(action),
          icon: action.type === 'capture_screenshot' ? <Camera size={16} aria-hidden /> : <Icon name="support" size={16} />,
          disabled: Boolean(pendingActionId) || isProcessing,
          titleAttribute: action.label,
          onSelect: () => handleAssistantAction(action),
        })),
      ]
    : composerActionDescriptors.filter(descriptor => purpose === 'BUSINESS' || !['report-draft', 'automation-draft'].includes(descriptor.id)).map(buildComposerMenuItem);
  const globalComposerMenuItems: AssistantComposerMenuItem[] = [
    ...contextualComposerMenuItems.map((item) => ({
      ...item,
      section: item.section || (isTurkishAssistant ? 'Yetenekler' : 'Capabilities'),
    })),
  ];
  const menuHistoryItems = historyItems.slice(0, 5);
  const historyBackLabel = isTurkishAssistant ? 'Geri' : 'Back';
  const historyAllProductsLabel = isTurkishAssistant ? 'Tümü' : 'All';
  const historyCurrentProductLabel = isTurkishAssistant ? 'Bu ürün' : 'This product';
  const historyActiveLabel = texts.showActiveTitle || (isTurkishAssistant ? 'Aktif' : 'Active');
  const historyArchivedLabel = isTurkishAssistant ? 'Arşivlenmiş' : 'Archived';
  const assistantMenuLabel = isTurkishAssistant ? 'AI menüsü' : 'AI menu';
  const closeAssistantLabel = isTurkishAssistant ? 'Asistanı kapat' : 'Close assistant';
  const viewAllHistoryLabel = isTurkishAssistant ? 'Tümünü göster' : 'Show all';
  const aiWorkspaceLabel = isTurkishAssistant ? 'AI ekranı' : 'AI workspace';
  const aiSettingsLabel = isTurkishAssistant ? 'Ayarlar' : 'Settings';
  const feedbackLabel = isTurkishAssistant ? 'Geri bildirim' : 'Feedback';

  const getConversationApplicationCode = (item: AssistantConversationSummary): string =>
    isObjectRecord(item.context) && typeof item.context.applicationCode === 'string'
      ? item.context.applicationCode
      : '';

  const formatConversationMetaText = (item: AssistantConversationSummary): string =>
    [
      dateTime.formatDateTime(item.updatedAt || item.createdAt),
      getConversationApplicationCode(item),
    ].filter(Boolean).join(' · ');

  const renderConversationList = (items: AssistantConversationSummary[], compact: boolean) => {
    if (historyLoading) return <div className="qp-global-voice-empty">{texts.processing}</div>;
    if (historyError) return <div className="qp-global-voice-empty">{historyError}</div>;
    if (items.length === 0) {
      return <div className="qp-global-voice-empty">{texts.noHistoryMessage || 'Kayıtlı konuşma bulunmuyor.'}</div>;
    }

    return items.map((item) => {
      const title = item.title || texts.title;
      const meta = formatConversationMetaText(item);
      if (compact) {
        return (
          <button
            key={String(item.id)}
            type="button"
            className="qp-global-voice-nav-history-item"
            onClick={() => void openConversation(item.id)}
            title={title}
          >
            <span className="qp-global-voice-nav-history-name">{title}</span>
            <span className="qp-global-voice-nav-history-meta">{meta}</span>
          </button>
        );
      }

      return (
        <div key={String(item.id)} className="qp-global-voice-history-item">
          <button
            type="button"
            className="qp-global-voice-history-main"
            onClick={() => void openConversation(item.id)}
            title={title}
          >
            <span className="qp-global-voice-history-name">{title}</span>
            <span className="qp-global-voice-history-meta">{meta}</span>
          </button>
          <div className="qp-global-voice-history-actions">
            <button
              type="button"
              className="qp-global-voice-icon-btn"
              onClick={() => void archiveConversation(item.id, !item.archivedAt)}
              title={item.archivedAt
                ? texts.unarchiveConversationTitle || 'Arşivden çıkar'
                : texts.archiveConversationTitle || 'Arşivle'}
              aria-label={item.archivedAt
                ? texts.unarchiveConversationTitle || 'Arşivden çıkar'
                : texts.archiveConversationTitle || 'Arşivle'}
              disabled={historyLoading}
            >
              {item.archivedAt ? <RotateCcw size={15} aria-hidden /> : <Archive size={15} aria-hidden />}
            </button>
            <button
              type="button"
              className="qp-global-voice-icon-btn"
              onClick={() => void deleteConversation(item.id, title)}
              title={texts.deleteConversationTitle || 'Sil'}
              aria-label={texts.deleteConversationTitle || 'Sil'}
              disabled={historyLoading}
            >
              <Trash2 size={15} aria-hidden />
            </button>
          </div>
        </div>
      );
    });
  };

  const panelResizeBounds = resolveGlobalAssistantPanelResizeBounds();
  const panelResizeValue = Math.round(
    panelWidth ?? resolveGlobalAssistantPresetWidth(isExpanded),
  );
  const panelUsesAlternateWidth = isExpanded || panelWidth != null;
  const panelResizeLabel = feedbackLanguage === 'tr'
    ? 'QubePro AI panelini yeniden boyutlandır'
    : 'Resize the QubePro AI panel';

  return (
    <>
      <style>{CSS}</style>
      <input
        ref={fileInputRef}
        type="file"
        className="qp-global-voice-file-input"
        multiple
        onChange={(event) => void handleFiles(event.target.files)}
        tabIndex={-1}
        aria-hidden="true"
      />
      {!engaged || !isOpen ? null : (
        <div
          ref={rootRef}
          className={`qp-global-voice-root ${isExpanded ? 'expanded' : ''} ${isResizing ? 'resizing' : ''}`}
          style={panelWidth == null ? undefined : {
            '--qp-global-voice-panel-width': `${panelWidth}px`,
          } as CSSProperties}
        >
          <div
            className="qp-global-voice-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label={panelResizeLabel}
            aria-controls="qp-global-voice-panel"
            aria-valuemin={panelResizeBounds.min}
            aria-valuemax={panelResizeBounds.max}
            aria-valuenow={panelResizeValue}
            tabIndex={0}
            onPointerDown={handlePanelResizePointerDown}
            onPointerMove={handlePanelResizePointerMove}
            onPointerUp={handlePanelResizePointerEnd}
            onPointerCancel={handlePanelResizePointerEnd}
            onKeyDown={handlePanelResizeKeyDown}
            onDoubleClick={resetPanelWidth}
          />
          <div
            id="qp-global-voice-panel"
            className={`qp-global-voice-panel ${isOpen ? 'open' : ''} ${isExpanded ? 'expanded' : ''}`}
          >
            <div className="qp-global-voice-header">
              <div className="qp-global-voice-title">
                <button
                  type="button"
                  className={`qp-global-voice-menu-toggle ${assistantMenuOpen ? 'active' : ''}`}
                  onClick={() => void toggleAssistantMenu()}
                  title={assistantMenuLabel}
                  aria-label={assistantMenuLabel}
                  aria-expanded={assistantMenuOpen}
                >
                  <Menu size={19} aria-hidden />
                </button>
                <span className="qp-global-voice-heading">{GLOBAL_ASSISTANT_BRAND_TITLE}</span>
              </div>
              <div className="qp-global-voice-header-actions">
                <button
                  type="button"
                  className={`qp-global-voice-icon-btn ${panelUsesAlternateWidth ? 'active' : ''}`}
                  onClick={() => {
                    if (panelWidth != null) {
                      resetPanelWidth();
                      return;
                    }
                    setIsExpanded((prev) => !prev);
                  }}
                  title={panelUsesAlternateWidth ? ((texts as any).collapseTitle || 'Daralt') : ((texts as any).expandTitle || 'Genişlet')}
                  aria-label={panelUsesAlternateWidth ? ((texts as any).collapseTitle || 'Daralt') : ((texts as any).expandTitle || 'Genişlet')}
                  aria-pressed={panelUsesAlternateWidth}
                >
                  {panelUsesAlternateWidth ? <Minimize2 size={18} aria-hidden /> : <Maximize2 size={18} aria-hidden />}
                </button>
                <button
                  type="button"
                  className="qp-global-voice-close"
                  onClick={() => setIsOpen(false)}
                  title={closeAssistantLabel}
                  aria-label={closeAssistantLabel}
                >
                  <Icon name="close" size={20} />
                </button>
              </div>
            </div>

            {assistantMenuOpen ? (
              <div className={`qp-global-voice-nav-panel ${historyOpen ? 'history-mode' : ''}`}>
                {historyOpen ? (
                  <>
                    <div className="qp-global-voice-nav-panel-head">
                      <button
                        type="button"
                        className="qp-global-voice-nav-back"
                        onClick={() => void closeFullHistoryView()}
                        title={historyBackLabel}
                        aria-label={historyBackLabel}
                      >
                        <ChevronLeft size={18} aria-hidden />
                      </button>
                      <div className="qp-global-voice-nav-title">{texts.historyOpenTitle || texts.historyTitle || 'Geçmiş'}</div>
                    </div>

                    <div className="qp-global-voice-history-filters">
                      <div className="qp-global-voice-history-filter-group" role="group" aria-label="Konuşma durumu">
                        <button
                          type="button"
                          className={`qp-global-voice-history-filter ${!showArchivedConversations ? 'active' : ''}`}
                          onClick={() => !showArchivedConversations ? undefined : void toggleArchivedFilter()}
                          disabled={historyLoading}
                          aria-label={historyActiveLabel}
                          aria-pressed={!showArchivedConversations}
                        >
                          {historyActiveLabel}
                        </button>
                        <button
                          type="button"
                          className={`qp-global-voice-history-filter ${showArchivedConversations ? 'active' : ''}`}
                          onClick={() => showArchivedConversations ? undefined : void toggleArchivedFilter()}
                          disabled={historyLoading}
                          aria-label={historyArchivedLabel}
                          aria-pressed={showArchivedConversations}
                        >
                          {historyArchivedLabel}
                        </button>
                      </div>
                      <div className="qp-global-voice-history-filter-group" role="group" aria-label="Ürün filtresi">
                        <button
                          type="button"
                          className={`qp-global-voice-history-filter ${historyProductScope === 'all' ? 'active' : ''}`}
                          onClick={() => historyProductScope === 'all' ? undefined : void changeHistoryProductScope('all')}
                          disabled={historyLoading}
                          aria-label={historyAllProductsLabel}
                          aria-pressed={historyProductScope === 'all'}
                        >
                          {historyAllProductsLabel}
                        </button>
                        <button
                          type="button"
                          className={`qp-global-voice-history-filter ${historyProductScope === 'current' ? 'active' : ''}`}
                          onClick={() => historyProductScope === 'current' ? undefined : void changeHistoryProductScope('current')}
                          disabled={historyLoading}
                          aria-label={historyCurrentProductLabel}
                          aria-pressed={historyProductScope === 'current'}
                        >
                          {historyCurrentProductLabel}
                        </button>
                      </div>
                    </div>

                    <div className="qp-global-voice-history-list">
                      {renderConversationList(historyItems, false)}
                    </div>
                  </>
                ) : (
                  <>
                <button
                  type="button"
                  className="qp-global-voice-nav-command primary"
                  onClick={startNewConversation}
                  disabled={isProcessing}
                  title={texts.newConversationTitle || 'Yeni sohbet'}
                  aria-label={texts.newConversationTitle || 'Yeni sohbet'}
                >
                  <MessageSquarePlus size={16} aria-hidden />
                  <span>{texts.newConversationTitle || 'Yeni sohbet'}</span>
                </button>

                <div className="qp-global-voice-nav-section">
                  <div className="qp-global-voice-nav-section-head">
                    <span>{texts.historyTitle || 'Geçmiş'}</span>
                    <button
                      type="button"
                      className="qp-global-voice-nav-link"
                      onClick={() => void openFullHistoryView()}
                      title={viewAllHistoryLabel}
                      aria-label={viewAllHistoryLabel}
                    >
                      {viewAllHistoryLabel}
                    </button>
                  </div>
                  <div className="qp-global-voice-nav-history-list">
                    {historyLoading ? (
                      <div className="qp-global-voice-empty">{texts.processing}</div>
                    ) : historyError ? (
                      <div className="qp-global-voice-empty">{historyError}</div>
                    ) : menuHistoryItems.length === 0 ? (
                      <div className="qp-global-voice-empty">{texts.noHistoryMessage || 'Kayıtlı konuşma bulunmuyor.'}</div>
                    ) : menuHistoryItems.map((item) => {
                      const title = item.title || texts.title;
                      const meta = [
                        dateTime.formatDateTime(item.updatedAt || item.createdAt),
                        isObjectRecord(item.context) && typeof item.context.applicationCode === 'string'
                          ? item.context.applicationCode
                          : '',
                      ].filter(Boolean).join(' · ');
                      return (
                        <button
                          key={String(item.id)}
                          type="button"
                          className="qp-global-voice-nav-history-item"
                          onClick={() => void openConversation(item.id)}
                          title={title}
                        >
                          <span className="qp-global-voice-nav-history-name">{title}</span>
                          <span className="qp-global-voice-nav-history-meta">{meta}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="qp-global-voice-nav-grid">
                  {false ? (
                  <button
                    type="button"
                    className="qp-global-voice-nav-command"
                    onClick={() => void toggleArchivedFilter()}
                    disabled={historyLoading}
                  >
                    <Archive size={16} aria-hidden />
                    <span>{showArchivedConversations ? (texts.showActiveTitle || 'Aktif') : (texts.showArchivedTitle || 'Arşiv')}</span>
                  </button>
                  ) : null}
                  <button
                    type="button"
                    className="qp-global-voice-nav-command"
                    onClick={openAiWorkspace}
                    title={aiWorkspaceLabel}
                    aria-label={aiWorkspaceLabel}
                  >
                    <ExternalLink size={16} aria-hidden />
                    <span>{aiWorkspaceLabel}</span>
                  </button>
                  <button
                    type="button"
                    className="qp-global-voice-nav-command"
                    onClick={openAiSettings}
                    title={aiSettingsLabel}
                    aria-label={aiSettingsLabel}
                  >
                    <Settings size={16} aria-hidden />
                    <span>{aiSettingsLabel}</span>
                  </button>
                  <button
                    type="button"
                    className="qp-global-voice-nav-command"
                    onClick={openFeedbackSupportForm}
                    title={feedbackLabel}
                    aria-label={feedbackLabel}
                  >
                    <Flag size={16} aria-hidden />
                    <span>{feedbackLabel}</span>
                  </button>
                </div>
                  </>
                )}
              </div>
            ) : null}

            {false && historyOpen ? (
              <div className="qp-global-voice-history-panel">
                <div className="qp-global-voice-history-toolbar">
                  <div className="qp-global-voice-history-title">{texts.historyTitle || 'Geçmiş'}</div>
                  <button
                    type="button"
                    className="qp-global-voice-history-filter"
                    onClick={() => void toggleArchivedFilter()}
                    disabled={historyLoading}
                  >
                    {showArchivedConversations
                      ? texts.showActiveTitle || 'Aktif'
                      : texts.showArchivedTitle || 'Arşiv'}
                  </button>
                </div>
                <div className="qp-global-voice-history-list">
                  {historyLoading ? (
                    <div className="qp-global-voice-empty">{texts.processing}</div>
                  ) : historyError ? (
                    <div className="qp-global-voice-empty">{historyError}</div>
                  ) : historyItems.length === 0 ? (
                    <div className="qp-global-voice-empty">{texts.noHistoryMessage || 'Kayıtlı konuşma bulunmuyor.'}</div>
                  ) : historyItems.map((item) => {
                    const title = item.title || texts.title;
                    const meta = [
                      item.assistant?.name,
                      dateTime.formatDateTime(item.updatedAt || item.createdAt),
                      isObjectRecord(item.context) && typeof item.context.applicationCode === 'string'
                        ? item.context.applicationCode
                        : '',
                    ].filter(Boolean).join(' · ');
                    return (
                      <div key={String(item.id)} className="qp-global-voice-history-item">
                        <button
                          type="button"
                          className="qp-global-voice-history-main"
                          onClick={() => void openConversation(item.id)}
                          title={title}
                        >
                          <span className="qp-global-voice-history-name">{title}</span>
                          <span className="qp-global-voice-history-meta">{meta}</span>
                        </button>
                        <div className="qp-global-voice-history-actions">
                          <button
                            type="button"
                            className="qp-global-voice-icon-btn"
                            onClick={() => void archiveConversation(item.id, !item.archivedAt)}
                            title={item.archivedAt
                              ? texts.unarchiveConversationTitle || 'Arşivden çıkar'
                              : texts.archiveConversationTitle || 'Arşivle'}
                            aria-label={item.archivedAt
                              ? texts.unarchiveConversationTitle || 'Arşivden çıkar'
                              : texts.archiveConversationTitle || 'Arşivle'}
                            disabled={historyLoading}
                          >
                            {item.archivedAt ? <RotateCcw size={15} aria-hidden /> : <Archive size={15} aria-hidden />}
                          </button>
                          <button
                            type="button"
                            className="qp-global-voice-icon-btn"
                            onClick={() => void deleteConversation(item.id, title)}
                            title={texts.deleteConversationTitle || 'Sil'}
                            aria-label={texts.deleteConversationTitle || 'Sil'}
                            disabled={historyLoading}
                          >
                            <Trash2 size={15} aria-hidden />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <AssistantPurposeSelector value={purpose} onChange={changeConversationPurpose} language={language} />
            <div className="qp-global-voice-body">
              {messages.map((msg) => (
                <div key={msg.id} className={`qp-global-voice-msg-wrap ${msg.role}`}>
                  {msg.role === 'user' && msg.targetAssistant ? (
                    <div className="qp-global-voice-routing-label">
                      @{msg.targetAssistant.name}
                    </div>
                  ) : null}
                  {msg.role === 'ai' && (
                    msg.assistant
                    || msg.routingMode === 'EXPERT_BOARD'
                    || msg.routingMode === 'EXPERT_360'
                  ) ? (
                    <div className="qp-global-voice-routing-label">
                      {msg.assistant
                        ? `${msg.assistant.name} ${isTurkishAssistant ? 'yanıtladı' : 'answered'}`
                        : texts.title}
                      {msg.routingMode === 'EXPERT_360'
                        ? ` · ${isTurkishAssistant ? '360° Analiz' : '360° analysis'}`
                        : msg.routingMode === 'EXPERT_BOARD'
                          ? ` · ${isTurkishAssistant ? 'Uzman Masası' : 'Expert desk'}`
                        : ''}
                    </div>
                  ) : null}
                  <div className={`qp-global-voice-msg ${msg.role}`}>
                    {msg.content}
                  </div>
                  {msg.role === 'ai' && msg.expertContributions?.length ? (
                    <div
                      className="qp-global-voice-expert-contributions"
                      aria-label={isTurkishAssistant ? 'Uzman katkıları' : 'Expert contributions'}
                    >
                      {msg.expertContributions.map((contribution) => (
                        <span
                          key={`${msg.id}-${contribution.assistant.id}`}
                          className={`qp-global-voice-expert-contribution ${contribution.status.toLowerCase()}`}
                          title={contribution.question
                            || contribution.limitations.join(' ')
                            || contribution.assistant.description
                            || contribution.assistant.name}
                        >
                          @{contribution.assistant.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {msg.role === 'ai' && msg.dataView ? (
                    <AssistantDataViewCard
                      view={msg.dataView}
                      presentation={msg.dataPresentation || 'TABLE'}
                      analysis={msg.verifiedAnalysis}
                    />
                  ) : null}
                  {msg.role === 'ai' && msg.sources?.length ? (
                    <AssistantDataSources sources={msg.sources} />
                  ) : null}
                  {msg.timestamp ? (
                    <time className="qp-global-voice-msg-time" dateTime={msg.timestamp}>
                      {dateTime.formatDateTime(msg.timestamp)}
                    </time>
                  ) : null}
                  {msg.role === 'ai' && getRenderableMessageActions(msg.actions).length ? (
                    <div className="qp-global-voice-actions" aria-label={texts.suggestedAction}>
                      {getRenderableMessageActions(msg.actions).map((action, index) => {
                        const actionId = `${msg.id}-${action.type}-${index}`;
                        return (
                          <button
                            key={actionId}
                            type="button"
                            className="qp-global-voice-action-btn"
                            onClick={() => handleAssistantAction(action, msg.proposalReceiptId)}
                            disabled={Boolean(pendingActionId) || isProcessing}
                            title={action.type === 'prepare_product_form' ? assistantProductFormCopy(language).open : action.label}
                          >
                            {action.type === 'prepare_product_form' ? (pendingActionId === 'product-form' ? assistantProductFormCopy(language).pending : assistantProductFormCopy(language).open) : action.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ))}
              {supportDescriptionMode && (
                <SupportTicketForm
                  descriptionRef={supportDescriptionRef}
                  targetLocked
                  language={language}
                  value={supportForm}
                  onChange={updateSupportFormField}
                  onClose={returnToMainMenu}
                  busy={isProcessing || isCapturingScreen}
                  onSubmit={() => void submitSupportFormDirect()}
                  canSubmit={supportFormHasContent(supportForm) && localScreenshot?.scope !== supportScopeRef.current.version}
                  submitDisabledReason={localScreenshot ? getScreenCaptureLabels(language).uploadFailed : undefined}
                  error={supportFormFailure ? supportSubmissionErrorMessage(supportFormFailure, language) : undefined}
                  onCaptureScreenshot={() => void captureScreen()}
                  onAttachFile={() => fileInputRef.current?.click()}
                  attachmentCount={attachments.length}
                />
              )}
              {localScreenshot?.scope === supportScopeRef.current.version && (
                <figure className="support-ticket-screenshot">
                  <img src={localScreenshot.url} alt={getScreenCaptureLabels(language).add} />
                  <figcaption>
                    <a className="btn-secondary" href={localScreenshot.url} download={localScreenshot.file.name}>
                      {getScreenCaptureLabels(language).download}
                    </a>
                    <button type="button" className="btn-secondary" aria-label={getScreenCaptureLabels(language).remove}
                      disabled={isCapturingScreen} onClick={() => setLocalScreenshot(null)}><X size={14} aria-hidden /></button>
                  </figcaption>
                </figure>
              )}
              {isProcessing && (
                <div className="qp-global-voice-msg-wrap ai">
                  <div className="qp-global-voice-msg ai">
                    <span className="qp-global-voice-processing">{texts.processing}</span>
                  </div>
                </div>
              )}
              <AssistantWorkStatus work={work} language={language} />
              <div ref={messagesEndRef} />
            </div>

            {attachments.length > 0 ? (
              <div className="qp-global-voice-attachments" aria-label="Ekler">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="qp-global-voice-attachment-chip">
                    <span className="qp-global-voice-attachment-name" title={attachment.name}>
                      {attachment.name}
                    </span>
                    <button
                      type="button"
                      className="qp-global-voice-attachment-remove"
                      onClick={() => setAttachments((prev) => prev.filter((item) => item.id !== attachment.id))}
                      title={getScreenCaptureLabels(language).remove}
                      aria-label={getScreenCaptureLabels(language).remove}
                    >
                      <X size={14} aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            {assistantPickerOpen ? (
              <AssistantIdentityPicker
                selected={targetAssistant}
                assistants={assistantPickerPurpose === 'BOARD'
                  ? availableAssistants.filter((assistant) => !assistant.isSystem && !assistant.isDefault)
                  : availableAssistants}
                open
                query={assistantPickerQuery}
                showTrigger={false}
                showSearchInput={assistantPickerPurpose === 'PINNED'}
                navigationTargetRef={inputRef}
                loading={assistantPickerLoading}
                error={assistantPickerError}
                disabled={isProcessing}
                language={language}
                onOpenChange={(open) => {
                  setAssistantPickerOpen(open);
                  if (!open) {
                    assistantPickerRequestIdRef.current += 1;
                    setAssistantPickerQuery('');
                    setAssistantPickerLoading(false);
                  }
                }}
                onQueryChange={beginAssistantPickerQuery}
                onSelect={handleAssistantSelection}
              />
            ) : null}

            {supportDescriptionMode || attachments.length > 0 ? (
              <div className="qp-global-voice-support-hint">
                {texts.supportDescriptionHint ||
                  'Destek kaydı için sorunu, beklediğiniz davranışı ve varsa hata mesajını buraya yazın.'}
              </div>
            ) : null}

            <AssistantComposerBar
              hideInput={supportDescriptionMode}
              menuOpen={composerMenuOpen}
              onMenuOpenChange={handleComposerMenuOpenChange}
              menuItems={globalComposerMenuItems}
              menuLabel={composerMenuLabel}
              menuDisabled={isProcessing || isCapturingScreen}
              inputRef={inputRef}
              value={inputValue}
              placeholder={
                supportDescriptionMode || attachments.length > 0
                  ? texts.supportTypingPlaceholder || 'Sorununuzu buraya yazın...'
                  : purpose === 'QUBEPRO_HELP' ? purposeTexts.placeholder : texts.typingPlaceholder
              }
              disabled={isProcessing || isCapturingScreen}
              onValueChange={handleComposerValueChange}
              onSubmit={submitComposer}
              isListening={isListening}
              micTitle={texts.micTitle}
              micAriaLabel={texts.micAria}
              onMicToggle={toggleListening}
              sendDisabled={
                (supportDescriptionMode ? Object.keys(validateSupportForm(supportForm)).length > 0 : !inputValue.trim())
                || (!supportDescriptionMode && workMode === 'EXPERT_BOARD' && !expertBoardReady)
                || isProcessing || isCapturingScreen || localScreenshot?.scope === supportScopeRef.current.version
                || (purpose === 'BUSINESS' && work.uncertain)
              }
              sendTitle={composerTexts.sendTitle}
              isProcessing={isChatRequestActive}
              stopTitle={composerTexts.stopTitle}
              onStop={stopActiveAssistantResponse}
              layout="stacked"
              actionsRowRef={footerActionsRef}
              leadingToken={targetAssistant ? (
                <AssistantTargetChip
                  name={targetAssistant.name}
                  disabled={isProcessing}
                  language={language}
                  onRemove={clearTargetAssistant}
                />
              ) : undefined}
              onLeadingTokenRemove={targetAssistant ? clearTargetAssistant : undefined}
              multiline
              inlineEditor
              mentions={composerMentions}
              onMentionsChange={(mentions) => {
                composerMentionsRef.current = mentions;
                setComposerMentions(mentions);
              }}
              workModeControl={purpose === 'BUSINESS' ? (
                <AssistantWorkModeSelector
                  mode={workMode}
                  open={workModeMenuOpen}
                  disabled={isProcessing || supportFlowActive}
                  language={language}
                  selectedAssistantName={targetAssistant?.name}
                  panelBoundaryRef={footerActionsRef}
                  onOpenChange={setWorkModeMenuOpen}
                  onSelectMode={handleWorkModeSelection}
                />
              ) : undefined}
              secondaryActions={(
                <button
                  type="button"
                  className={`qp-global-voice-audio-toggle ${speechReadEnabled ? 'active' : ''}`}
                  onClick={toggleSpeechRead}
                  title={speechReadEnabled ? texts.speechReadOnTitle : texts.speechReadOffTitle}
                  aria-label={speechReadEnabled ? texts.speechReadOnAria : texts.speechReadOffAria}
                  aria-pressed={speechReadEnabled}
                >
                  {speechReadEnabled ? <Volume2 size={18} aria-hidden /> : <VolumeX size={18} aria-hidden />}
                </button>
              )}
              classes={{
                row: 'qp-global-voice-footer',
                inputRow: 'qp-global-voice-footer-input-row',
                actionsRow: 'qp-global-voice-footer-actions',
                secondaryActions: 'qp-global-voice-footer-secondary',
                menuWrapper: 'qp-global-voice-plus-wrap',
                menuButton: 'qp-global-voice-mic-btn',
                menuButtonActive: 'active',
                menuPanel: 'qp-global-voice-plus-menu',
                menuItem: 'qp-global-voice-plus-menu-item',
                inputShell: 'qp-global-voice-input-shell',
                input: 'qp-global-voice-input',
                micButton: 'qp-global-voice-inline-btn',
                micActive: 'listening',
                sendButton: 'qp-global-voice-send-btn',
              }}
            />
          </div>
        </div>
      )}
      {draftReview && purpose === 'BUSINESS' && draftReview.scope === supportScopeRef.current.version && draftReview.conversationId === conversationId ?
        <AssistantDraftActionReview surfaceScope="viewport" action={draftReview.action} language={language} busy={isProcessing} error={draftReview.error}
          onClose={() => setDraftReview(null)} onConfirm={confirmDraftAction} /> : null}
    </>
  );
}
